// ---------- home: puzzle selection ----------
// Screen shown at startup (and reopenable from the panel): lists the puzzles published
// from administration and loads the chosen one into the current game. If the backend
// is unreachable or doesn't have any published puzzle yet, the screen reports it but
// blocks nothing — the demo image loaded by main.ts stays playable underneath.

import { el, escapeHtml, icon } from './html';
import { ALPHABETS, alphabetKnown } from '../shared/alphabets';
import { currentUser } from './account';
import { CATEGORY_LABELS, categoryMarkId, isCategorySlug } from './category';
import {
  HttpError,
  deleteCreation,
  fetchPuzzle,
  fetchPuzzles,
  isCreation,
  type PuzzleDetail,
  type PuzzleSummary,
} from './api';
import {
  availablePackCount,
  forgetFreshlyUnlocked,
  isFreshlyUnlocked,
  openPacksPage,
  paintTeaserFan,
  teaserPuzzleCount,
} from './packs';
import { techniqueIconLg } from './techniqueIcon';
import {
  forgetProgress,
  leaveSheet,
  loadProgress,
  needsPull,
  progressIndex,
  targetFingerprint,
  type CardProgress,
  type Saved,
} from './progress';
import { DIFFICULTIES, gridDifficulty, type Difficulty } from '../shared/difficulty';
import { estimateMinutes } from '../shared/effort';
import { animPoke, animReplayStop, noMotion } from './anim';
import { cancelPush, pullPuzzle, settleCreation, syncOnce } from './sync';
import {
  getCreation,
  hasCreation,
  listCreations,
  putCreation,
  removeCreation,
} from './creationStore';
import { alphabetLabel, dialParts, difficultyLabel, fmtMinutes, t, type I18nKey } from './i18n';
import { refreshPrefs } from './prefs';
import { forgetDailyOrigin, openDaily, openedFromDaily, refreshDaily, wireDaily } from './daily';
import { invalidateLayers, updateProgress, render, resetPlay } from './render';
import { buildLegend, replayFinished, startPlay } from './ui';
import { S } from './state';
import { showBreakAdIfDue, showInterstitialIfReady } from './ads';
import {
  askForReview,
  canShowRateCard,
  dismissRateCard,
  markRateCardShown,
  openStoreListing,
  rateCardStarted,
  rateCardVariant,
} from './review';
import { rebuildThumb } from './thumbs';
import { paintCover, type CoverSource } from './packCover';
import { closePiece } from './piece';
import { INTRO_ID, INTRO_ZONES, introSheet, openIntroPiece } from './intro';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

/** Difficulty, read off the grid — see difficulty.ts, which the generator shares: a puzzle's
 *  level is a fact about its size, not a field somebody filled in. */
function difficulty(p: PuzzleSummary): Difficulty {
  return gridDifficulty(p.gridW, p.gridH);
}

/** Les hues de la charte « Sorbet » déjà en jeu ailleurs sur l'écran (accent + pastels des
 *  tags) — pas de teinte inédite pour la salve, elle ne fait que reprendre à voix haute les
 *  couleurs que la carte porte déjà. */
const CONFETTI_COLORS = ['#c9f24b', '#e4ff8a', '#f39ac2', '#86d9c4', '#b6a3f0'];
const EASE_BACK = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** Salve de confettis décorative au premier contact sur une carte — voir buildCard(). Posée
 *  dans #fxLayer, hors de .home, pour ne pas dépendre du sort de l'écran d'accueil. Exportée :
 *  src/packs.ts la réutilise telle quelle pour l'ouverture du coffre plutôt que d'en écrire une
 *  seconde. */
export function confettiBurst(x: number, y: number): void {
  if (noMotion()) return;
  const layer = document.getElementById('fxLayer');
  if (!layer) return;
  for (let i = 0; i < 14; i++) {
    const piece = document.createElement('span');
    piece.className = 'confetti-piece';
    piece.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
    piece.style.left = `${x}px`;
    piece.style.top = `${y}px`;
    layer.appendChild(piece);
    const angle = Math.random() * Math.PI * 2;
    const dist = 30 + Math.random() * 60;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist - 24;
    const rot = Math.random() * 560 - 280;
    const anim = piece.animate(
      [
        { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx}px, ${dy + 84}px) rotate(${rot}deg)`, opacity: 0 },
      ],
      { duration: 650 + Math.random() * 300, easing: 'cubic-bezier(.25,.7,.3,1)' },
    );
    anim.onfinish = () => piece.remove();
  }
}

/** Les cartes tombent en place à chaque repeinture du plateau — un temps du bout du monde
 *  plafonné (24 cartes max de décalage), pour rester une garniture et pas une attente sur une
 *  grande étagère. */
function popIn(container: HTMLElement): void {
  if (noMotion()) return;
  const cards = container.querySelectorAll<HTMLElement>('.home-card');
  cards.forEach((card, i) => {
    card.animate(
      [
        { opacity: 0, transform: 'scale(0.6)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 320, delay: Math.min(i, 24) * 18, easing: EASE_BACK, fill: 'both' },
    );
  });
}

/** Rayon de l'arc du cadran, r=19,5 dans un carré de 50 (voir .home-clock dans style.css). */
const DIAL_R = 19.5;
const DIAL_C = 2 * Math.PI * DIAL_R;

/**
 * L'HORLOGE : un seul objet pour ce qu'il reste À FAIRE et pour ce qui est DÉJÀ fait.
 *
 * Elle remplace l'anneau de progression, qui portait un pourcentage. Le pourcentage répondait à
 * « où en suis-je » ; les minutes restantes répondent à « est-ce que je finis maintenant », qui
 * est la question qu'on se pose devant une étagère. L'arc dit toujours la proportion, on ne perd
 * donc rien — seul change ce qui est écrit au milieu.
 *
 * Le MÊME cadran sert aux deux états, et c'est ce qui a fait retenir cette forme : sur une feuille
 * jamais ouverte, l'arc devient un pointillé et le centre porte la durée totale. Rien à ajouter
 * ailleurs sur la carte, donc la ligne du bas reste à DEUX objets — la catégorie et la technique —
 * et le débordement mesuré en allemand ne revient pas.
 *
 * `Math.max(2, pct)` comme pour l'anneau d'avant : à 1 %, l'arc était plus court que son propre
 * bout arrondi et disparaissait, ce qui se lit « pas commencé » sur une feuille qui l'est.
 */
function clockDial(minutes: number, pct: number | null): string {
  const { n, u } = dialParts(minutes);
  const arc =
    pct === null
      ? `<circle class="ctrack dash" cx="25" cy="25" r="${DIAL_R}"></circle>`
      : `<circle class="ctrack" cx="25" cy="25" r="${DIAL_R}"></circle>
      <circle class="cfill" cx="25" cy="25" r="${DIAL_R}"
        stroke-dasharray="${DIAL_C}" stroke-dashoffset="${DIAL_C * (1 - Math.max(2, pct) / 100)}"></circle>`;
  return `
    <svg class="home-clock" viewBox="0 0 50 50" aria-hidden="true">
      <circle class="cbg" cx="25" cy="25" r="23.5"></circle>
      ${arc}
      <text class="cn" x="25" y="26">${n}</text>
      <text class="cu" x="25" y="37">${u}</text>
    </svg>`;
}

/** The category a puzzle counts under. Missing — or a slug the validator wouldn't accept, which
 *  is the same thing as far as this screen goes — lands in NO_CAT rather than in nothing: those
 *  puzzles must stay reachable from some button. */
function catKey(p: PuzzleSummary): string {
  return isCategorySlug(p.category) ? p.category : NO_CAT;
}

function catLabel(key: string): string {
  if (key === NO_CAT) return t('home.category.other');
  return CATEGORY_LABELS[key] ?? key;
}

function applyPuzzle(
  id: string,
  fp: string,
  gridW: number,
  gridH: number,
  alphabetId: string,
  target: number[][],
): void {
  // Une pièce quittée en cours (src/piece.ts) laisserait sinon ses crochets sur la feuille
  // suivante, qui s'enregistrerait sous le grand puzzle de la pièce.
  closePiece();
  S.puzzleId = id;
  S.puzzleFp = fp;
  S.alphabetId = alphabetId;
  S.gridW = gridW;
  S.gridH = gridH;
  S.target = target;
  S.viewport = { z: 1, x: 0, y: 0 };
  S.lockedSymbol = null;
  S.hover = null;
  S.hoverKey = '';
  buildLegend();
  invalidateLayers();
  resetPlay();
}

/**
 * Replays the saved taps to rebuild the filled state, silently — no animation, no sound, no
 * score: this is not the player filling cells, it's the sheet as they left it. One tap fills one
 * cell, so the replay is a plain assignment and nothing about it can drift. A tap pointing
 * outside the grid or at a cell with nothing to paint is skipped rather than fatal: a save from
 * an older build shouldn't cost the whole puzzle.
 *
 * Saves written before the zone fill was dropped hold only the SEED of each zone, and come back
 * as the cells actually touched — a sheet in progress reopens sparser than it was left. Nothing
 * is lost or corrupted; there is simply more left to paint, under the rule that now applies.
 */
function restore(saved: Saved): void {
  for (const idx of saved.taps) {
    const r = Math.floor(idx / S.gridW);
    const c = idx % S.gridW;
    if (r >= S.gridH || c >= S.gridW) continue;
    if (!S.target[r][c] || S.filled[r][c]) continue;
    S.filled[r][c] = true;
    S.taps.push(idx);
  }
  S.gameOver = saved.done;
  S.rating = saved.rating ?? 0;
  invalidateLayers();
  render();
  updateProgress();
  // A finished puzzle is worth reopening for one reason: watching it draw itself again, in the
  // order it was painted. The taps just restored ARE that order. No fanfare and no card —
  // that was celebrated when it was earned — only the replay and the beat that closes it: the
  // grid fades out and the drawing stands bare, which is where this used to start.
  if (saved.done) replayFinished();
}

/**
 * La grille, avec UNE reprise automatique.
 *
 * C'est ce que le joueur faisait déjà à la main : ouvrir une feuille, tomber sur « indisponible »,
 * toucher « Réessayer », et voir la même feuille s'ouvrir. Sur un téléphone, une requête perdue
 * n'est pas une exception, c'est le régime normal d'un ascenseur ou d'un tunnel — et le catalogue
 * en fait déjà autant pour lui-même. Une seule reprise, et courte : au-delà, c'est un écran qui
 * ment sur ce qu'il est en train d'attendre.
 *
 * PAS sur un 404. Celui-là est une réponse, pas un incident : la feuille n'est pas servie à ce
 * compte, la redemander ne changera rien, et attendre 400 ms pour l'apprendre deux fois ne rend
 * service à personne.
 */
async function fetchGrid(id: string): Promise<PuzzleDetail> {
  // La première feuille se lit dans l'application : c'est tout son intérêt (cf. src/intro.ts).
  if (id === INTRO_ID) {
    const s = introSheet();
    return {
      id,
      title: '',
      alphabetId: s.alphabetId,
      gridW: s.gridW,
      gridH: s.gridH,
      target: s.target,
    };
  }
  // Une création se lit sur le téléphone (src/creationStore.ts). Absente d'ici, c'est une de celles
  // que le serveur garde encore et que le rapatriement n'a pas pu ramener (cf. mergeCreations) :
  // elle s'ouvre par lui, comme avant ; s'il ne l'a plus, son 404 dit « plus disponible », comme
  // pour une feuille retirée du catalogue.
  if (isCreation(id)) {
    const mine = await getCreation(id).catch(() => null);
    if (mine) return mine;
  }
  try {
    return await fetchPuzzle(id, await currentUser());
  } catch (err) {
    if (err instanceof HttpError && err.status === 404) throw err;
    await new Promise((r) => setTimeout(r, 400));
    return fetchPuzzle(id, await currentUser());
  }
}

/** `fx`/`fy` are where the finger touched the card — passed through from buildCard() so the
 *  confetti it decorates can still burst from under the player's thumb, even though it now
 *  fires here, once the puzzle is actually confirmed open, rather than on the tap itself. */
/**
 * OUVRIR une feuille : la charger, restaurer ce qui a déjà été peint, et quitter le catalogue.
 *
 * Extraite de `selectPuzzle` pour que le calendrier du puzzle du jour (src/daily.ts) ouvre
 * exactement de la même façon — c'est la séquence délicate du jeu (le rattrapage nuage, la
 * restauration avant `startPlay`, l'ordre des deux) et la dupliquer aurait garanti qu'une des
 * deux copies dérive.
 *
 * Elle NE PEINT RIEN et LAISSE MONTER l'erreur : chaque appelant a son propre écran d'attente et
 * son propre endroit pour dire ce qui s'est passé (une étagère ici, une case de calendrier
 * là-bas).
 *
 * Les trois états d'une feuille — jamais touchée, en cours, terminée — passent tous par ici sans
 * distinction : `loadProgress` + `restore` suffisent, et une feuille finie s'ouvre donc d'elle-même
 * sur sa page de fin.
 */
export async function openPuzzle(id: string, fx: number, fy: number): Promise<void> {
  // The cloud copy is fetched ONLY when the local one is known to be incomplete — a fresh
  // install, or another device that went further. In the ordinary case the puzzle opens with
  // no network in the way, and the push that follows reconciles in the background.
  // Pas pour une création déjà sur le téléphone : sa sauvegarde n'existe qu'ici (cf. sync.ts). Une
  // que le serveur garde encore (rapatriement pas encore fait, cf. mergeCreations) a peut-être sa
  // partie là-bas seulement — après une réinstallation, elle s'ouvrait vide (revue du 2026-10-30).
  if (needsPull(id) && !(isCreation(id) && (await hasCreation(id).catch(() => false))))
    await pullPuzzle(id);
  // Le chien de l'initiation dont l'œil n'est pas fini (sa carte de « Mon flux », cf. introCard) :
  // c'est la PIÈCE qu'on rouvre, là où elle en était, et non le chien entier — elle a sa fin à
  // elle, le recul et le renard. openIntroPiece rend faux quand l'œil est fini : on ouvre alors
  // le chien entier comme n'importe quelle feuille.
  if (id === INTRO_ID && openIntroPiece(toLibrary)) {
    confettiBurst(fx, fy);
    closeHome();
    return;
  }
  const puzzle = await fetchGrid(id);
  const fp = targetFingerprint(puzzle.target);
  // Read before anything else: startPlay() runs resetPlay(), which clears the taps.
  const saved = loadProgress(id, fp);
  applyPuzzle(id, fp, puzzle.gridW, puzzle.gridH, puzzle.alphabetId, puzzle.target);
  startPlay();
  if (saved) restore(saved);
  // Only now — never on a puzzle that turns out to be unavailable, see the catch in
  // selectPuzzle. Le ruban s'éteint ici pour la même raison : une feuille qu'on n'a pas pu
  // ouvrir n'a pas été vue (cf. forgetFreshlyUnlocked dans packs.ts).
  forgetFreshlyUnlocked(id);
  confettiBurst(fx, fy);
  closeHome();
}

/** « Voir la bibliothèque », au bout de l'œil du chien rouvert depuis sa carte (cf. openPuzzle). */
function toLibrary(): void {
  openHomeOn('library');
}

/**
 * La carte du chien de l'initiation dans « Mon flux », en cours ou fini (2026-10-31).
 *
 * Le serveur ne le sert à personne — il est retiré (cf. src/introGrid.ts) —, donc il n'arrive
 * jamais dans le catalogue, et sans cette carte un joueur parti voir la bibliothèque au bout de
 * l'œil ne retrouvait plus son chien : ni en cours, ni une fois fini. Elle se fabrique ici, de la
 * grille livrée avec l'application, et SEULEMENT quand il y a du terrain couvert : à 0 %, le tri
 * des étagères la rangerait dans la Bibliothèque, où le chien n'a pas sa place.
 */
function introCard(): PuzzleSummary[] {
  const r = progressIndex().get(INTRO_ID);
  if (!r || (!r.done && r.pct <= 0)) return [];
  const s = introSheet();
  return [
    {
      id: INTRO_ID,
      title: '',
      alphabetId: s.alphabetId,
      gridW: s.gridW,
      gridH: s.gridH,
      zones: INTRO_ZONES,
      category: 'animaux',
      hasCover: false,
      createdAt: '2026-10-31',
      packId: null,
      packStarter: true,
    },
  ];
}

/**
 * Ouvre une feuille depuis un AUTRE écran que le catalogue — les chiffres de « Moi » et
 * d'« Ensemble » (cf. wireProgression). Le chemin d'une carte, et pas openPuzzle() seul : c'est
 * lui qui sait dire au joueur qu'une feuille a disparu ou que le réseau est tombé, sur l'écran
 * qu'il retrouve en fermant la progression.
 */
export function openFromElsewhere(id: string): void {
  void selectPuzzle(id, innerWidth / 2, innerHeight / 2);
}

/** La feuille est-elle dans le catalogue de ce joueur — donc ouvrable d'un tap ? Faux pour une
 *  feuille d'un pack qu'il n'a pas débloqué : la proposer finirait sur « indisponible ». */
export function inCatalogue(id: string): boolean {
  return lastServed.some((p) => p.id === id);
}

async function selectPuzzle(id: string, fx: number, fy: number): Promise<void> {
  const body = $('homeBody');
  body.replaceChildren(loading());
  try {
    // Une carte d'étagère : le retour de partie doit ramener sur l'étagère, pas sur le
    // calendrier, même si c'est par lui qu'on avait ouvert la feuille précédente.
    forgetDailyOrigin();
    await openPuzzle(id, fx, fy);
  } catch (err) {
    // DIRE LEQUEL DES DEUX. « Ce puzzle est indisponible » accusait la feuille de tout : un
    // 404, mais aussi un réseau tombé le temps d'une requête — et le joueur, lui, voyait sa
    // carte s'ouvrir une fois sur deux sans comprendre pourquoi. Seul un 404 est un fait sur
    // le puzzle ; le reste est un fait sur la ligne, et se dit avec le message qui existe déjà
    // pour ça ailleurs dans cet écran.
    const gone = err instanceof HttpError && err.status === 404;
    body.replaceChildren(
      empty(
        'warn',
        t(gone ? 'home.empty.puzzleGone' : 'home.empty.serverError'),
        t('home.empty.retry'),
        () => void loadPuzzles(),
        true,
      ),
    );
  }
}

/**
 * Neither the finished render nor the title: both give the subject away before a single cell
 * has been filled, and the whole point is to discover it by drawing it. The card is a sheet of
 * graph paper still folded — same paper, same grid as the game — with a question mark on it.
 *
 * The dimensions stay, because they spoil nothing and something has to tell the cards apart:
 * with the image and the title gone they would otherwise be indistinguishable, leaving no way
 * to choose one or to recognise one already played. They also stand in for a difficulty.
 */
function buildCard(p: PuzzleSummary, prog?: CardProgress): HTMLButtonElement {
  const btn = document.createElement('button');
  const done = !!prog?.done;
  const pct = prog?.pct ?? 0;
  const diff = difficulty(p);
  const started = !!prog && (prog.pct > 0 || prog.done);
  btn.className = `home-card${done ? ' done' : ''}`;
  // Son id sur la carte : c'est ce qui permet de la retrouver dans la grille pour la ramener
  // dans le champ au retour d'une feuille (cf. revealCard).
  btn.dataset['id'] = p.id;
  // ---------- la note de la ligne du bas : une durée, ou les dimensions ----------
  // COMBIEN DE TEMPS ÇA VA ME PRENDRE est la question qu'on se pose devant une étagère ;
  // « 110 × 110 » demande une conversion mentale que personne ne fait. La durée prend donc la
  // place des dimensions, qui restent dans l'infobulle — et la ligne du bas est déjà pleine, le
  // commentaire du gabarit plus bas raconte qu'une pastille de plus l'a fait passer sur deux rangs.
  //
  // Les dimensions restent le REPLI, et pas seulement par prudence : une feuille publiée avant la
  // colonne `zones` n'a pas de compte (cf. schema.ts), et laisser la case vide déplacerait la
  // pastille de technique d'une carte à l'autre dans la même grille. Une carte TERMINÉE garde
  // aussi ses dimensions : une durée sur un travail fini n'apprend rien à personne.
  //
  // L'estimation part de la progression en CASES là où le travail se compte en zones : le
  // catalogue ne connaît que `pct` et ne charge pas la grille pour l'affiner. Ça sous-estime un
  // peu — les grandes zones se peignent en premier — et c'est ce que l'arrondi grossier absorbe.
  const minutes = done ? 0 : estimateMinutes(p.zones ?? 0, pct);
  const tip = t('home.card.tooltip', { w: p.gridW, h: p.gridH, diff: difficultyLabel(diff.key) });
  btn.title = minutes
    ? `${tip} · ${t(started ? 'home.card.leftLong' : 'home.card.etaLong', { d: fmtMinutes(minutes) })}`
    : tip;
  // Touched puzzles show the sheet as far as the player took it. That reveals nothing they
  // haven't drawn themselves, and it is what tells one card from another far better than a
  // number; the thumbnail is a data: URL produced locally, never a cover coming from the
  // server.
  //
  // Untouched ones montraient le glyphe de leur CATÉGORIE, en grand (remplacé depuis par une
  // couverture, voir plus bas). C'était l'illustration de
  // technique jusqu'ici, et l'échange n'est pas cosmétique : la première question devant une
  // étagère est « de quoi ça parle », pas « avec quels symboles ». La technique n'a pas disparu
  // pour autant : elle est passée en second rôle (la ligne de métadonnées d'alors, retirée depuis
  // avec la refonte des cartes ; c'est le filtre par technique de la Bibliothèque qui la porte).
  //
  // La règle qui tenait pour le « ? » puis pour la technique tient toujours : un glyphe de
  // catégorie ne montre RIEN du dessin — « la grille ne dit pas ce qu'elle cache ».
  // Plus de `✓` géant quand un puzzle terminé n'a PAS de vignette : ça arrive pour une
  // progression venue du serveur (un autre appareil, une réinstallation), puisque la vignette
  // reste locale — et l'étagère « Terminés » devenait alors un mur de coches sur du crème vide,
  // exactement ce que l'illustration de technique avait remplacé côté puzzles neufs. La carte
  // montre donc sa technique, et c'est le sceau ci-dessous qui dit « fini ».
  const face = prog?.thumb
    ? `<img src="${prog.thumb}" alt="">`
    : // LA COUVERTURE DES PACKS, à l'échelle d'un puzzle (2026-10-23) : la teinte de sa
      // catégorie et une feuille de biais dans la texture de SON alphabet. Le glyphe de
      // catégorie, seul au milieu du crème, faisait une étagère de pictogrammes ; la vitrine des
      // packs a montré qu'une feuille qui évoque la technique donne plus envie, sans rien dire
      // du sujet — la règle de packCover.ts vaut ici aussi. Peinte à l'arrivée dans le champ
      // (coverObserver), le canevas n'ayant pas de taille avant d'être dans la page.
      //
      // Les CRÉATIONS aussi, depuis le même jour : leur marque d'outil seule détonnait à côté des
      // couvertures de la Bibliothèque. Sans catégorie, elles prennent la teinte de la Sélection ;
      // la marque de l'outil reste, en petit, dans le coin de la catégorie (categoryOver).
      `<canvas class="home-pcover" data-pcover="${p.id}" aria-hidden="true"></canvas>`;
  if (!prog?.thumb)
    coverSources.set(p.id, {
      id: p.id,
      category: p.category,
      alphabets: [{ alphabetId: p.alphabetId, count: 1 }],
    });
  // How far along, as a number and not only as a 3px bar: "am I nearly there" is the question
  // this shelf exists to answer. It rides WITH the bar rather than in the meta line above,
  // where it pushed the category tag onto a second line and left the cards of a row uneven.
  // (Déclaré plus haut : l'infobulle en a besoin pour choisir entre « en tout » et « restant ».)
  // PLUS DE PLAQUE DE DIFFICULTÉ. Elle disait la taille de la grille par un mot ; la durée le dit
  // en minutes, ce que personne n'a besoin de convertir. Les trois paliers n'ont pas disparu pour
  // autant — ils tiennent toujours le filtre et le tri (voir facets()), où ils servent à réduire
  // une liste, ce qu'une durée ferait moins bien. Le coin haut gauche de la couverture est donc
  // libre, et c'est ce qui laisse la place au ruban de l'autre côté sans que rien ne se dispute.
  //
  // La ligne du bas ne porte plus la durée : elle est passée dans le cadran, sur la feuille. Ce
  // sont la CATÉGORIE et la TECHNIQUE qui l'occupent — deux objets, jamais trois, la règle n'a
  // pas bougé.
  // LE PACK D'OÙ VIENT LA FEUILLE, dans Mon flux seulement (2026-10-23) : la Bibliothèque le dit
  // déjà par son rangement, et Créer n'a que des créations. Toujours une ligne, même pour une
  // feuille gratuite (« Pour commencer ») : une carte sans elle serait plus basse que ses voisines
  // et décalerait la rangée.
  const from = tab === 'flux' ? `<span class="home-from">${escapeHtml(packLabel(p))}</span>` : '';
  btn.innerHTML = `
    <span class="home-cover">
      ${face}
      ${categoryOver(p)}
      ${tab === 'library' && isFreshlyUnlocked(p.id) ? `<span class="home-ruban"><i>${t('home.card.new')}</i></span>` : ''}
      ${!done && minutes ? clockDial(minutes, started ? pct : null) : ''}
      ${done ? `<span class="home-seal" aria-hidden="true"><svg class="i"><use href="#i-seal"></use></svg></span>` : ''}
    </span>
    ${from}
  `;
  btn.onclick = (e) => void selectPuzzle(p.id, e.clientX, e.clientY);
  const cv = btn.querySelector<HTMLCanvasElement>('canvas.home-pcover');
  if (cv) coverObserver().observe(cv);
  return btn;
}

/**
 * La catégorie posée sur la feuille, en bas à gauche : son GLYPHE, en blanc, comme sur les packs
 * (.pk-glyph). Une pastille de texte en surimpression a duré une version (2026-10-23) ; sur une
 * couverture qui prend toute la carte, elle faisait étiquette collée, et le glyphe suffit — c'est
 * lui que le filtre Catégorie montre déjà. Le libellé reste pour les lecteurs d'écran. Plus de
 * ligne de technique sous la carte non plus : la couverture la montre en grand.
 */
function categoryOver(p: PuzzleSummary): string {
  const mine = isCreation(p.id);
  const id = mine ? 'i-creation' : categoryMarkId(p.category);
  const label = mine ? p.title || t('home.card.mine') : catLabel(catKey(p));
  return `<svg class="home-cat-over" role="img" aria-label="${escapeHtml(label)}"><use href="#${id}"></use></svg>`;
}

/** Ce que paintCover() doit savoir de chaque carte neuve, rempli par buildCard(). Une entrée par
 *  feuille du catalogue, réécrite et non ajoutée d'un passage à l'autre : bornée par sa taille. */
const coverSources = new Map<string, CoverSource>();

/**
 * Les couvertures DÉJÀ PEINTES, en image, par feuille et par taille de carte. paint() reconstruit
 * toutes les cartes à chaque onglet ou filtre touché : sans ce cache, chaque couverture visible
 * se repeignait case par case à chaque fois.
 *
 * Et pourquoi une IMAGE et pas le canevas lui-même (revue du 2026-10-30) : le canevas peint
 * restait dans la carte, avec sa mémoire, jusqu'à la fermeture de l'écran — jusqu'à 2,5 pixels
 * par point, soit ~0,5 à 1 Mo par carte, et une Bibliothèque d'une centaine de cartes faisait
 * passer les WebView d'entrée de gamme au-delà de ce qu'elles tiennent (rechargement, canevas
 * blanc : c'est la mesure que packs.ts consigne au-dessus de paintCovers). Une fois peinte, la
 * couverture est encodée (WebP, ~10 ko) puis le canevas est rendu : une <img> hors de l'écran,
 * le navigateur sait en libérer l'image décodée ; un canevas, jamais.
 */
const coverImages = new Map<string, string>();
/** Assez pour toute la Bibliothèque à une taille de carte, et une rotation de l'écran ; au-delà
 *  la plus ancienne part (une Map garde l'ordre d'insertion), ~10 ko chacune. */
const COVER_IMAGES_MAX = 300;

let pcoverObs: IntersectionObserver | null = null;
/** Les couvertures se peignent à l'approche du champ, comme celles des packs (cf. paintCovers
 *  dans packs.ts) : une Bibliothèque de cent cartes ne paie que celles qu'on voit. */
function coverObserver(): IntersectionObserver {
  return (pcoverObs ??= new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const cv = e.target as HTMLCanvasElement;
        pcoverObs?.unobserve(cv);
        // Pas de taille (carte cachée) : rien à peindre, comme paintCover() le dirait lui-même.
        if (!cv.clientWidth || !cv.clientHeight) continue;
        const id = cv.dataset['pcover'] ?? '';
        const key = `${id}@${cv.clientWidth}x${cv.clientHeight}`;
        let url = coverImages.get(key);
        if (!url) {
          const src = coverSources.get(id);
          if (!src) continue;
          paintCover(cv, src, 7, true);
          url = cv.toDataURL('image/webp', 0.85);
          coverImages.set(key, url);
          if (coverImages.size > COVER_IMAGES_MAX)
            coverImages.delete(coverImages.keys().next().value!);
        }
        const img = document.createElement('img');
        img.className = 'home-pcover';
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.src = url;
        cv.replaceWith(img);
        cv.width = cv.height = 0; // rendu tout de suite, sans attendre le ramasse-miettes
      }
    },
    { rootMargin: '160px' },
  ));
}

// ---------- shelves ----------
// Three states, and a puzzle is in exactly one of them. `started` is not "has a record" but
// "has ground covered": starting over leaves a record at 0%, and such a puzzle belongs back on
// the library shelf, not in a flux of work that isn't in progress.

/** Les quatre onglets, dans l'ordre de la barre. « prefs » en est un depuis que Réglages a cessé
 *  d'être un dialogue, et « daily » depuis le puzzle du jour : même barre, même rang, leur propre
 *  défilement. Ce ne sont PAS des étagères pour autant — elles ne portent pas de puzzles du
 *  catalogue — d'où les gardes de paint() et de shelf() plus bas, qui sont le prix de les avoir
 *  mises dans le même type plutôt que dans un état à côté. Le prix est le bon : l'onglet allumé
 *  reste une seule variable, et il ne peut pas se désaccorder d'un second drapeau. */
type Tab = 'flux' | 'daily' | 'library' | 'create' | 'prefs';
/** Les trois onglets qui portent une étagère de cartes. Les autres (« daily », « prefs ») ont
 *  leur propre page et sortent de paint() avant tout ce qui sert une étagère.
 *
 *  « create » depuis le 2026-10-16 : la création depuis une photo est une fonctionnalité PHARE, et
 *  rangée dans la Bibliothèque (une carte en tête, un filtre « Mes créations ») elle passait
 *  inaperçue. Elle a pris la place de Réglages dans la barre, et Réglages est monté en icône à
 *  côté du trophée (#prefsOpen) — on ouvre ses réglages bien moins souvent qu'on ne crée. */
type Shelf = Extract<Tab, 'flux' | 'library' | 'create'>;
type Filter = 'doing' | 'done';

interface Shelves {
  doing: PuzzleSummary[];
  done: PuzzleSummary[];
  fresh: PuzzleSummary[];
}

/** The two values of the category filter that aren't a category: everything, and the puzzles
 *  without one. Neither can collide with a real slug — SLUG demands a leading letter — so all
 *  three kinds share one field. */
const ALL = '';
const NO_CAT = '-';

let tab: Tab = 'flux';
/** La dernière ÉTAGÈRE visitée, pour que « précédent » depuis Réglages ou depuis le calendrier y
 *  revienne au lieu de quitter l'application. Ni 'prefs' ni 'daily' : c'est toute son utilité. */
let lastShelf: Shelf = 'flux';
/** Le dernier retour catalogue était SOLLICITÉ : une pub de pause s'y est montrée, ou il suivait
 *  une feuille finie (l'interstitielle de fin). Posé par openHome(), lu par paint() : la carte
 *  d'avis ne naît jamais sur un retour pareil. Le cas « feuille finie » doit être ici et pas
 *  seulement dans canShowRateCard() : depuis que la session se clôt après 30 minutes d'écran
 *  éteint (review.ts), une feuille finie AVANT une longue pause appartient à une session passée
 *  — « il est revenu » devient vrai sur le retour même qui joue l'interstitielle de fin. */
let solicitedLanding = false;
let filter: Filter = 'doing';
/** Combien de packs restent à débloquer — connu de manière asynchrone (cf.
 *  refreshPacksBanner()), lu de manière synchrone par paint() comme le reste de son état. 0
 *  jusqu'à la première réponse : le bandeau reste caché plutôt que de clignoter au chargement. */
let packsAvailable = 0;
let cat: string = ALL;
/** Difficulty filter, ALL for "toutes". Shares its neutral value with the category filter: both
 *  are "no choice made", and neither key can be the empty string. */
let diffFilter: string = ALL;
/** Alphabet filter, ALL for "tous". Same neutral value again — an alphabet id is a slug from the
 *  registry, never empty. */
let alphaFilter: string = ALL;

/** The three narrowing rows. */
type FacetKey = 'cat' | 'diff' | 'alpha';

/**
 * Those rows, most recently tapped first. Any two of them — or all three — can describe a shelf
 * that holds nothing: "Animaux", "Avancé" and "Dés" when no animal is both big and drawn in dice.
 * Something has to give, and it is never the tap that was just made — the player asked for that
 * one, and taking it back under their finger is the one behaviour that reads as a bug. So paint()
 * clears from the TAIL of this list: the choice made longest ago is the one that goes first.
 */
let recent: FacetKey[] = ['cat', 'diff', 'alpha'];

function pick(facet: FacetKey, key: string): void {
  if (facet === 'cat') cat = key;
  else if (facet === 'diff') diffFilter = key;
  else alphaFilter = key;
  recent = [facet, ...recent.filter((f) => f !== facet)];
}

function clearFacet(facet: FacetKey): void {
  if (facet === 'cat') cat = ALL;
  else if (facet === 'diff') diffFilter = ALL;
  else alphaFilter = ALL;
}
/** Last list fetched. Kept so switching tabs is instant instead of hitting the network for a
 *  partition of data already in hand. */
let shelves: Shelves = { doing: [], done: [], fresh: [] };

/** La dernière liste servie, gardée pour pouvoir REPEINDRE SANS RÉSEAU. C'est elle qui permet à
 *  un retour de feuille de retrouver l'étagère telle qu'elle était, au lieu d'un squelette. */
let lastServed: PuzzleSummary[] = [];
let progress = new Map<string, CardProgress>();

/**
 * Les CRÉATIONS du joueur (src/creer.ts), telles que GET /puzzles?mine=1 les a rendues avec le
 * catalogue — du plus récent au plus ancien. Elles entrent aussi dans `lastServed`, et donc dans les étagères du flux
 * dès qu'elles sont commencées : une feuille qu'on peint est une feuille qu'on peint, d'où qu'elle
 * vienne. La BIBLIOTHÈQUE, elle, ne les mêle pas au catalogue — elle est ce que le jeu propose, pas
 * ce qu'on y a apporté. Elles ont leur onglet, « Créer », qui les montre toutes quel que soit leur
 * état, sous le bouton qui en fabrique une nouvelle.
 */
let creations: PuzzleSummary[] = [];

/** Les créations changent (une de plus, une de moins) : tout ce qui en découle suit, au même
 *  endroit — l'étagère servie, l'avancement par feuille, l'ordre des étagères. Écrit deux fois à
 *  l'identique (création et suppression) jusqu'à la revue du 2026-10-17 : un correctif posé sur
 *  l'une aurait manqué l'autre. */
function setCreations(next: PuzzleSummary[], served: PuzzleSummary[]): void {
  creations = next;
  lastServed = served;
  progress = progressIndex();
  shelves = sort(orderPuzzles(lastServed));
}

/**
 * Partitionne le catalogue en trois étagères, et ordonne les deux du flux par RÉCENCE : la
 * dernière feuille touchée en haut.
 *
 * Pourquoi la récence pour celles-là et pas pour la bibliothèque : le flux est une liste de
 * choses qu'on a faites, et ce qu'on cherche dedans est presque toujours la dernière — on y
 * revient pour reprendre, ou pour revoir ce qu'on vient de finir. La bibliothèque, elle, est une
 * liste de choses qu'on n'a pas faites : il n'y a aucune date à lire dessus, donc elle garde
 * l'ordre d'orderPuzzles() (difficulté, alphabet, catégorie), qui propose une progression.
 *
 * La date vient de `at` dans le stockage de progression, écrite par tous les chemins d'écriture
 * et reprise du serveur quand la sauvegarde arrive de là (cf. sa déclaration dans progress.ts).
 * Une progression sans date — il ne devrait plus y en avoir — descend en bas plutôt que de
 * remonter en tête : `''` est inférieur à n'importe quelle chaîne ISO.
 */
function sort(list: readonly PuzzleSummary[]): Shelves {
  const out: Shelves = { doing: [], done: [], fresh: [] };
  for (const p of list) {
    const prog = progress.get(p.id);
    if (prog?.done) out.done.push(p);
    else if (prog && prog.pct > 0) out.doing.push(p);
    else out.fresh.push(p);
  }
  const at = (p: PuzzleSummary): string => progress.get(p.id)?.at ?? '';
  // Comparaison de chaînes et non de Date : ce sont des ISO, donc l'ordre lexicographique EST
  // l'ordre chronologique, sans construire deux objets par comparaison.
  const byRecency = (a: PuzzleSummary, b: PuzzleSummary): number =>
    at(a) < at(b) ? 1 : at(a) > at(b) ? -1 : 0;
  out.doing.sort(byRecency);
  out.done.sort(byRecency);
  return out;
}

/**
 * LES TROIS NARROWINGS NARROWENT LA BIBLIOTHÈQUE, ET RIEN D'AUTRE.
 *
 * La catégorie en a toujours été là ; la difficulté et l'alphabet couraient sur toutes les
 * étagères, au motif que « je n'ai qu'un quart d'heure » et « pas les dés » sont d'aussi bonnes
 * questions devant ce qu'on a commencé que devant ce qu'on n'a pas touché. L'usage a tranché
 * autrement : le flux ne contient pas des centaines de feuilles — on n'a pas cent puzzles en
 * cours — donc il n'y a rien à y chercher, et la rangée de boutons y coûtait une ligne d'écran
 * pour une aide qui n'arrivait jamais. Le flux se lit maintenant par récence (cf. sort()), ce
 * qui est le seul tri dont une liste courte de choses faites ait besoin.
 *
 * Les valeurs sont GARDÉES et non remises à zéro quand le joueur part vers le flux : il revient
 * à l'étagère qu'il a laissée, et un filtre qui se réinitialise dans votre dos est un filtre
 * qu'on cesse de croire. Le test sur l'onglet suffit à ce qu'ils ne s'appliquent pas ailleurs —
 * ce qui compte, parce qu'un filtre invisible qui trie quand même est bien pire qu'un filtre
 * visible qui ne sert à rien.
 */
function matchCat(p: PuzzleSummary): boolean {
  return tab !== 'library' || cat === ALL || catKey(p) === cat;
}

function matchDiff(p: PuzzleSummary): boolean {
  return tab !== 'library' || diffFilter === ALL || difficulty(p).key === diffFilter;
}

function matchAlpha(p: PuzzleSummary): boolean {
  return tab !== 'library' || alphaFilter === ALL || p.alphabetId === alphaFilter;
}

/** The shelf the player is looking at, before either filter. */
function shelf(): readonly PuzzleSummary[] {
  // Réglages n'a pas d'étagère. paint() n'appelle jamais la suite dans cet onglet (il branche
  // avant), mais la fonction est publique dans ce module et le type l'autorise : mieux vaut une
  // liste vide qu'un shelves['prefs'] indéfini le jour où quelqu'un l'appelle d'ailleurs.
  if (tab === 'prefs' || tab === 'daily') return [];
  if (tab === 'create') return creations.filter((c) => lastServed.some((p) => p.id === c.id));
  if (tab === 'library') return shelves.fresh.filter((p) => !isCreation(p.id));
  return filter === 'doing' ? shelves.doing : shelves.done;
}

// The three functions below answer the same question for their own row — WHICH buttons stand on
// this shelf, in which order. The known values (declared in the app, not discovered from data)
// always show, whether or not the current shelf has a puzzle in them: a testeur must be able to
// see the whole space of filters to know what's coming, not just what already exists. Only the
// VALUES A BUILD DOESN'T KNOW ABOUT — a category or alphabet id added in administration after
// this build shipped — still come from the data, since there is no other way to learn they exist.

/** Difficulties, in the order of the scale. The full scale, always: it's a closed set of three
 *  tiers derived from grid size (difficulty.ts), nothing external can add a fourth. */
function diffKeys(): string[] {
  return DIFFICULTIES.map((d) => d.key);
}

/** Alphabets, in the order the registry declares them — the same order as the game's own alphabet
 *  buttons, so the two lists never read as two different scales. The full registry, always; ids
 *  this build doesn't know come after, alphabetically, and those DO depend on what's on the
 *  shelf — a sheet drawn with an alphabet added later must stay reachable from a button. */
function alphaKeys(list: readonly PuzzleSummary[]): string[] {
  const present = new Set(list.map((p) => p.alphabetId).filter(Boolean));
  const known = ALPHABETS.map((a) => a.id);
  // `alphabetKnown` et non `!alphabetById` : celui-ci retombe sur les motifs et ne rend donc
  // JAMAIS une valeur fausse, ce qui laissait ce `rest` toujours vide — le bouton promis par le
  // commentaire ci-dessus n'apparaissait pas. (Le filtre de loadPuzzles écarte aujourd'hui ces
  // feuilles en amont ; ce cas reste là pour un id qui arriverait par un autre chemin.)
  const rest = [...present].filter((id) => !alphabetKnown(id)).sort();
  return [...known, ...rest];
}

/** Categories: the ones the game knows first, in the order they are declared, then "Autres" — a
 *  bucket, not a subject — then the ones it doesn't know, alphabetically. The known part and
 *  "Autres" show always; a slug this build has never seen still depends on the shelf, same
 *  reasoning as alphaKeys(). */
function catKeys(list: readonly PuzzleSummary[]): string[] {
  const present = new Set(list.map(catKey));
  const known = Object.keys(CATEGORY_LABELS);
  const rest = [...present].filter((k) => k !== NO_CAT && !(k in CATEGORY_LABELS)).sort();
  return [...known, NO_CAT, ...rest];
}

/** A value's position in a declared order, last if it isn't in it at all — used below to turn
 *  the three key lists above into comparators without listing the values twice. */
function rankIn<T>(keys: readonly T[]): (v: T) => number {
  const idx = new Map(keys.map((k, i) => [k, i]));
  return (v) => idx.get(v) ?? keys.length;
}

/** Card order within a shelf: complexity, then alphabet, then category. Reuses the exact
 *  universes the facets already compute (diffKeys()/alphaKeys()/catKeys()) so the shelf and the
 *  filters can never drift into two different orderings of the same three things.
 *
 *  À LA UNE D'ABORD (2026-10-14) : une feuille cochée dans l'admin passe devant tout le reste —
 *  c'est le premier puzzle qu'un nouveau joueur voit en ouvrant la bibliothèque. Sans ce
 *  premier critère, rien ne permettait de le choisir : le tri place Briques, dernier alphabet du
 *  registre, après toutes les feuilles de même difficulté. La mise en avant ne vaut que pour les
 *  feuilles pas encore commencées : les étagères du flux se lisent par récence (cf. sort()). */
function orderPuzzles(list: readonly PuzzleSummary[]): PuzzleSummary[] {
  const diffRank = rankIn(diffKeys());
  const alphaRank = rankIn(alphaKeys(list));
  const catRank = rankIn(catKeys(list));
  return [...list].sort(
    (a, b) =>
      Number(!!b.featured) - Number(!!a.featured) ||
      diffRank(difficulty(a).key) - diffRank(difficulty(b).key) ||
      alphaRank(a.alphabetId) - alphaRank(b.alphabetId) ||
      catRank(catKey(a)) - catRank(catKey(b)),
  );
}

/**
 * Three narrowing rows used to mean three permanent rows of chips — up to three lines above a
 * shelf that might hold four cards. One button per narrowing instead: it shows the current
 * choice (or the axis's name when there isn't one) and opens #facetSheet, which carries the
 * same chips the row used to — same paint()/pick(), only where the values are chosen moves from
 * "always on screen" to "on demand".
 *
 * `dot` is the difficulty axis's, whose chips carry the colour worn by the plate on the cards so
 * the sheet and the shelf below say the same thing twice rather than asking to be connected.
 */
const FACET_CONF: Record<
  FacetKey,
  { title: string; neutral: string; label: (key: string) => string; dot: boolean }
> = {
  cat: {
    title: t('home.facet.category.title'),
    neutral: t('home.facet.category.all'),
    label: catLabel,
    dot: false,
  },
  diff: {
    title: t('home.facet.difficulty.title'),
    neutral: t('home.facet.difficulty.all'),
    label: difficultyLabel,
    dot: true,
  },
  alpha: {
    title: t('home.facet.alphabet.title'),
    neutral: t('home.facet.alphabet.all'),
    label: alphabetLabel,
    dot: false,
  },
};
const FACET_BTN: Record<FacetKey, string> = {
  cat: 'facetCat',
  diff: 'facetDiff',
  alpha: 'facetAlpha',
};

/** The keys standing on each axis as of the last paint() — the sheet reads from here rather
 *  than recomputing when it opens, since it opens on demand and paint() already did the work. */
const facetKeys: Record<FacetKey, string[]> = { cat: [], diff: [], alpha: [] };
/** The narrowing whose sheet is open, null the rest of the time. */
let openFacet: FacetKey | null = null;

function currentOf(facet: FacetKey): string {
  return facet === 'cat' ? cat : facet === 'diff' ? diffFilter : alphaFilter;
}

/** One value is not a choice — a button would only restate what every card already says — so a
 *  narrowing's button appears from two keys onward and hides itself otherwise, same rule the
 *  row used to follow. */
function updateFacetButton(facet: FacetKey, current: string, keys: readonly string[]): void {
  facetKeys[facet] = [...keys];
  const btn = $(FACET_BTN[facet]);
  if (keys.length < 2) {
    btn.hidden = true;
    // The sheet it fed is now showing a narrowing that no longer applies — the library tab was
    // left, say, while its category sheet was open.
    if (openFacet === facet) closeFacetSheet();
    return;
  }
  const conf = FACET_CONF[facet];
  btn.hidden = false;
  btn.classList.toggle('on', current !== ALL);
  btn.replaceChildren();
  if (conf.dot && current !== ALL) {
    const i = document.createElement('i');
    i.className = `ddot diff-${current}`;
    btn.appendChild(i);
  }
  const label = document.createElement('span');
  label.className = 'flabel';
  // A text node, not innerHTML: an unknown slug (an alphabet id, a category) is shown as-is and
  // comes from the server.
  label.textContent = current === ALL ? conf.title : conf.label(current);
  btn.appendChild(label);
  const car = document.createElement('span');
  car.className = 'fcar';
  car.textContent = '▾';
  btn.appendChild(car);
  btn.onclick = () => openFacetSheet(facet);
  // The shelf just repainted under an open sheet (a filter picked elsewhere narrowed it further):
  // its chip list has to catch up rather than go stale behind the one just closed.
  if (openFacet === facet) renderFacetSheet(facet);
}

/**
 * Le filtre Alphabet est le seul des trois narrowings dont chaque valeur a une VRAIE
 * illustration (cf. techniqueIconLg() dans techniqueIcon.ts) — le même quadrillage 2×2 que le
 * coin d'une card de bibliothèque, en grand, plutôt que la puce à 22px réservée à Catégorie/
 * Difficulté. D'où une grille de tuiles au lieu d'une liste de lignes, cf. .home-cats-grid dans
 * style.css. « Tous » n'illustre rien : ce n'est pas une technique, juste l'absence de filtre —
 * sa tuile garde .fg-cover (même gabarit que les autres, pour que la grille reste régulière)
 * mais vide.
 */
function alphaTile(current: string, key: string): HTMLButtonElement {
  const conf = FACET_CONF.alpha;
  const b = document.createElement('button');
  b.classList.toggle('on', current === key);
  const cover = document.createElement('span');
  cover.className = 'fg-cover';
  if (key !== ALL) cover.insertAdjacentHTML('beforeend', techniqueIconLg(key));
  b.appendChild(cover);
  const label = document.createElement('span');
  label.className = 'fg-label';
  label.textContent = key === ALL ? conf.neutral : conf.label(key);
  b.appendChild(label);
  b.onclick = () => {
    pick('alpha', key);
    closeFacetSheet();
    paint();
  };
  return b;
}

function renderFacetSheet(facet: FacetKey): void {
  const conf = FACET_CONF[facet];
  const current = currentOf(facet);
  $('facetSheetTitle').textContent = conf.title;
  const body = $('facetSheetBody');
  body.classList.toggle('home-cats-grid', facet === 'alpha');

  if (facet === 'alpha') {
    body.replaceChildren(
      alphaTile(current, ALL),
      ...facetKeys.alpha.map((k) => alphaTile(current, k)),
    );
    return;
  }

  const chip = (key: string): HTMLButtonElement => {
    const b = document.createElement('button');
    b.classList.toggle('on', current === key);
    if (conf.dot && key !== ALL) {
      const i = document.createElement('i');
      i.className = `ddot diff-${key}`;
      b.appendChild(i);
    }
    b.appendChild(document.createTextNode(key === ALL ? conf.neutral : conf.label(key)));
    b.onclick = () => {
      pick(facet, key);
      closeFacetSheet();
      paint();
    };
    return b;
  };
  body.replaceChildren(chip(ALL), ...facetKeys[facet].map(chip));
}

function openFacetSheet(facet: FacetKey): void {
  openFacet = facet;
  renderFacetSheet(facet);
  $('facetSheet').setAttribute('aria-hidden', 'false');
}

/** Returns true when it actually closed something — same contract as closePrefs(), for the same
 *  reason: the Android back button needs to know whether it consumed the gesture. */
export function closeFacetSheet(): boolean {
  if (openFacet === null) return false;
  openFacet = null;
  $('facetSheet').setAttribute('aria-hidden', 'true');
  return true;
}

/** Le groupe d'une feuille dans la Bibliothèque : son pack, ou '' pour « Pour commencer », qui
 *  réunit les feuilles gratuites et celles du pack de démarrage — offertes à tous l'une comme
 *  l'autre, le joueur n'a aucune raison de les distinguer. */
function packKey(p: PuzzleSummary): string {
  return p.packId && !p.packStarter ? p.packId : '';
}

/** Le nom du pack d'une feuille, tel que la vitrine l'écrit (« Merveilles du monde n°2 »). Une
 *  feuille gratuite est « Pour commencer », une création « Mes créations ». Une feuille de pack
 *  dont le serveur n'a pas envoyé le nom (cache d'avant la mise à jour) n'en a pas : une ligne
 *  vide plutôt qu'un nom faux, le temps que le catalogue se recharge. */
function packLabel(p: PuzzleSummary): string {
  if (isCreation(p.id)) return t('home.facet.mine');
  if (!packKey(p)) return t('home.pack.base');
  if (!p.packName) return '\u00a0';
  return p.packVolume ? t('packs.volume', { title: p.packName, n: p.packVolume }) : p.packName;
}

/**
 * LA BIBLIOTHÈQUE RANGÉE PAR PACK (2026-10-23) : un titre par pack, ses feuilles neuves dessous,
 * dans l'ordre de orderPuzzles() à l'intérieur de chaque groupe. Rien d'autre ne change — mêmes
 * feuilles (les neuves), mêmes filtres, mêmes gestes : un joueur ne doit pas chercher où sont
 * passées ses feuilles. Seul le rangement dit maintenant d'où elles viennent ; avant, un pack
 * débloqué se dissolvait dans la grille et son nom disparaissait.
 *
 * L'ordre des groupes : « Pour commencer » d'abord — son nom le dit, et chez un joueur ancien il
 * s'est vidé à mesure qu'il commençait ses feuilles, donc il ne pousse rien vers le bas bien
 * longtemps ; puis un pack qui porte une feuille « À la une » (elle était en tête de la grille) ;
 * puis les packs, le plus récent d'abord.
 * Un joueur sans pack n'a qu'un groupe, et il n'a pas de titre : sa Bibliothèque est exactement
 * celle d'avant.
 */
function libraryShelves(list: readonly PuzzleSummary[]): HTMLElement[] {
  const groups = new Map<string, PuzzleSummary[]>();
  for (const p of list) {
    const key = packKey(p);
    groups.set(key, [...(groups.get(key) ?? []), p]);
  }
  // Pas de titre pour un groupe unique « Pour commencer » — un joueur sans pack retrouve sa
  // Bibliothèque d'avant. Un pack seul, lui, garde son nom : c'est tout l'objet du rangement, et
  // ses cartes ne le portent pas (revue du 2026-10-23).
  if (groups.size === 1 && groups.has('')) return [grid(list)];
  // L'âge d'un pack se lit sur TOUTES ses feuilles du catalogue, commencées comprises : sur les
  // seules neuves, commencer la plus récente le faisait reculer d'un rang (revue du 2026-10-23).
  const newestOf = new Map<string, string>();
  for (const p of lastServed) {
    const k = packKey(p);
    if (p.createdAt > (newestOf.get(k) ?? '')) newestOf.set(k, p.createdAt);
  }
  const newest = (key: string): string => newestOf.get(key) ?? '';
  const keys = [...groups.keys()].sort((a, b) => {
    const ga = groups.get(a)!;
    const gb = groups.get(b)!;
    return (
      Number(b === '') - Number(a === '') ||
      Number(gb.some((p) => p.featured)) - Number(ga.some((p) => p.featured)) ||
      newest(b).localeCompare(newest(a))
    );
  });
  return keys.map((key) => {
    const g = groups.get(key)!;
    const section = document.createElement('section');
    section.className = 'home-pack';
    const h = document.createElement('h2');
    h.className = 'home-pack-h';
    h.innerHTML = `<span>${escapeHtml(packLabel(g[0]))}</span><small>${g.length}</small>`;
    section.append(h, grid(g));
    return section;
  });
}

function grid(list: readonly PuzzleSummary[]): HTMLElement {
  const g = document.createElement('div');
  g.className = 'home-grid';
  for (const p of list) {
    const card = buildCard(p, progress.get(p.id));
    g.appendChild(tab === 'create' ? withDelete(card, p) : card);
  }
  return g;
}

/**
 * Une carte de « Mes créations », avec de quoi la SUPPRIMER — le seul endroit où on peut le faire,
 * et il le faut : le jeu plafonne le nombre de créations (CREATIONS_PER_ACCOUNT, src/creer.ts), et le message qui le
 * dit renvoie ici. Un bouton à côté de la carte et non dedans : une carte est déjà un <button>, et
 * un bouton dans un bouton n'est pas du HTML.
 *
 * `confirm()` plutôt qu'un dialogue de la charte : le geste est rare, il est destructif, et la
 * fenêtre du système est celle que le joueur sait lire pour « es-tu sûr ».
 */
function withDelete(card: HTMLButtonElement, p: PuzzleSummary): HTMLElement {
  const wrap = el('div', 'home-mine');
  const del = el('button', 'home-mine-del', icon('i-trash'));
  del.setAttribute('aria-label', t('home.creation.delete'));
  del.title = t('home.creation.delete');
  del.onclick = () => {
    if (!confirm(t('home.creation.deleteConfirm', { title: p.title || t('home.card.mine') })))
      return;
    void (async () => {
      try {
        await removeCreation(p.id);
      } catch {
        return; // la carte reste : rien n'a été supprimé
      }
      // Et la copie du serveur, si le rapatriement (importServerCreations) n'a pas encore pu
      // l'effacer. NOTÉE d'abord (cf. creationsDeleted) : si l'effacement échoue — hors ligne —,
      // le rapatriement suivant ne la ramènera pas, il réessaiera de l'effacer.
      setCreationDeleted(p.id, true);
      void currentUser().then((token) => {
        if (token) eraseOnServer(token, p.id).catch(() => {});
      });
      cancelPush(p.id);
      forgetProgress(p.id);
      setCreations(
        creations.filter((c) => c.id !== p.id),
        lastServed.filter((c) => c.id !== p.id),
      );
      paint();
    })();
  };
  wrap.append(card, del);
  return wrap;
}

/** An empty shelf has to say what to do next. The button is the point: without it, "rien ici"
 *  is a dead end on the very screen meant to send you somewhere. Defaults to opening the
 *  library — the one action every empty state used to lead to, before an empty *filter*
 *  result (see resetFilters()) needed a different one. `err` is for a request that failed
 *  rather than a shelf that's honestly empty — same layout, tinted red, so a network hiccup
 *  reads as a sentence on the page rather than the plain, un-styled `<p>` it used to be.
 *
 *  `icon` est un identifiant du sprite (cf. la note en fin de index.html), là où c'était un
 *  glyphe posé dans le texte : les quatre états portaient `!`, `?`, `✓` et 🔍 — le dernier étant
 *  le tout dernier emoji de l'application, avec le dessin de chaque téléphone et aucun accord
 *  avec la charte. */
function empty(
  icon: string,
  text: string,
  cta?: string,
  onClick: () => void = () => show('library'),
  err = false,
): HTMLElement {
  const el = document.createElement('div');
  el.className = `home-empty${err ? ' err' : ''}`;
  el.innerHTML = `<span class="home-q"><svg class="i" aria-hidden="true"><use href="#i-${icon}"></use></svg></span><p>${text}</p>`;
  if (cta) {
    const b = document.createElement('button');
    b.className = 'btn';
    b.textContent = cta;
    b.onclick = onClick;
    el.appendChild(b);
  }
  return el;
}

/** Loading, styled like every other state on this screen instead of a bare line of text — the
 *  three dots are decorative only (see .home-dots in style.css), never information, so cutting
 *  their animation under prefers-reduced-motion loses nothing. */
function loading(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'home-empty';
  el.innerHTML = `
    <span class="home-dots" aria-hidden="true"><i></i><i></i><i></i></span>
    <p>${t('home.loading')}</p>
  `;
  return el;
}

/** How many placeholder cards to draw while the catalogue itself is in flight — enough to fill
 *  a phone screen (the grid is `auto-fill`, so real screens hold anywhere from 2 to a dozen),
 *  never so many that a slow connection is scrolling skeletons for their own sake. */
const SKELETON_CARDS = 6;

/** The shape of what's coming, not a sentence about it: `loadPuzzles()`'s very first paint used
 *  to be a line of text where `popIn()` later bounces real cards in, on a screen that's
 *  otherwise worked hard for its motion (confetti, the progress ring, `popIn()` itself). A
 *  plain `<div>` per card, not a `<button>` — there's nothing behind it to press — and
 *  `aria-hidden` on the grid because the pulsing blocks say nothing a screen reader can use;
 *  `homeBody`'s own `aria-busy` (set by the caller) is what actually announces "loading". */
/**
 * CE QUE LA GRILLE MONTRE, en une chaîne — pour savoir si une repeinture apprendrait quelque
 * chose. Les cartes ne dépendent que de ça : la liste et son ordre, l'avancement, et la vignette
 * (présente ou non). Deux signatures égales veulent dire deux grilles identiques au pixel près.
 *
 * Comparer plutôt que repeindre, parce qu'une repeinture n'est pas gratuite : `replaceChildren`
 * jette toutes les cartes, `popIn()` les ré-anime, les vignettes se rechargent et le défilement
 * revient en haut. Repeindre à l'identique, c'est donc reprendre au joueur la place où il était.
 */
function shelfSignature(list: readonly PuzzleSummary[]): string {
  return list
    .map((p) => {
      const r = progress.get(p.id);
      // Le pack aussi : il range la Bibliothèque et titre les cartes de Mon flux. Un thème posé ou
      // un second pack du même thème (« Safari » devient « Safari n°1 ») en cours de session
      // restaient sinon sous l'ancien nom jusqu'au prochain changement d'onglet (revue du
      // 2026-10-23).
      return `${p.id}:${r?.pct ?? -1}:${r?.done ? 1 : 0}:${r?.thumb ? 1 : 0}:${packKey(p)}:${packLabel(p)}`;
    })
    .join('|');
}

function skeleton(): HTMLElement {
  const g = document.createElement('div');
  g.className = 'home-grid';
  g.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < SKELETON_CARDS; i++) {
    const c = document.createElement('div');
    c.className = 'home-card sk';
    c.innerHTML = `
      <span class="home-cover"></span>
    `;
    g.appendChild(c);
  }
  return g;
}

/** Now that the three narrowing rows list the whole known universe of values rather than only
 *  what the current shelf holds (see catKeys()/diffKeys()/alphaKeys()), picking one with nothing
 *  behind it — yet, or ever — is an expected outcome, not drift to paper over. Offered as the
 *  empty state's call to action instead of silently reverting the player's own tap. */
function resetFilters(): void {
  cat = ALL;
  diffFilter = ALL;
  alphaFilter = ALL;
  paint();
}

/**
 * La carte d'avis du flux — l'invitation VISIBLE vers la fiche du Store, là où la carte de Play
 * (askForReview) peut ne jamais s'afficher. Toutes ses règles — qui, quand, combien de fois —
 * vivent dans review.ts avec celles de sa jumelle ; ici on ne fait que la poser et l'écouter.
 *
 * En tête du flux, sous ses deux filtres, et nulle part ailleurs : c'est l'écran du retour,
 * celui où le joueur contemple ce qu'il a fait — la bibliothèque est l'écran où il CHERCHE, et
 * on n'interrompt pas quelqu'un qui choisit. Un seul chemin, pas de tri : le bouton ouvre la
 * fiche pour tout le monde, toutes les notes y restent possibles — c'est ce qui la garde dans
 * les règles de Play (pas de question-filtre, pas de récompense).
 */
/** Les trois textes de la carte, dans l'ordre où ils alternent (cf. rateCardVariant). Écrits en
 *  entier plutôt que construits (`home.rate.${n}.title`) : une clé construite échappe au typage
 *  des dictionnaires, et une faute de frappe y donnerait une carte vide. */
const RATE_TEXTS = [
  ['home.rate.1.title', 'home.rate.1.body', 'home.rate.1.cta'],
  ['home.rate.2.title', 'home.rate.2.body', 'home.rate.2.cta'],
  ['home.rate.3.title', 'home.rate.3.body', 'home.rate.3.cta'],
] as const satisfies ReadonlyArray<readonly [I18nKey, I18nKey, I18nKey]>;

function rateCard(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'rate-card';
  const [title, body, cta] = RATE_TEXTS[rateCardVariant() % RATE_TEXTS.length];
  el.innerHTML = `
    <button class="icon rate-close" title="${t('common.close')}" aria-label="${t('common.close')}"><svg class="i" aria-hidden="true"><use href="#i-close"></use></svg></button>
    <span class="rate-stars" aria-hidden="true">${'<svg class="i"><use href="#i-star"></use></svg>'.repeat(5)}</span>
    <p class="rate-title">${t(title)}</p>
    <p class="rate-body">${t(body)}</p>
    <button class="btn primary rate-go">${t(cta)}</button>
  `;
  el.querySelector<HTMLButtonElement>('.rate-close')!.onclick = () => {
    dismissRateCard();
    paint();
  };
  el.querySelector<HTMLButtonElement>('.rate-go')!.onclick = () => {
    // Éteinte AVANT d'ouvrir la fiche : le joueur a répondu, et au retour de Play l'écran ne
    // doit pas lui reposer la question qu'il vient de traiter.
    dismissRateCard();
    openStoreListing();
    paint();
  };
  markRateCardShown();
  return el;
}

/** Repaints the shelf, the tabs and the counters from `shelves`. No network, no fetching: this
 *  runs on every tap of a tab and has to be instant. */
function paint(): void {
  const body = $('homeBody');
  // Les cartes d'avant vont être remplacées : leurs couvertures pas encore peintes ne doivent plus
  // être suivies — l'observateur les gardait, elles et leur carte détachée, toute la session
  // (revue du 2026-10-30). buildCard() inscrit les nouvelles juste après.
  pcoverObs?.disconnect();
  const all = shelf();
  const matches = (p: PuzzleSummary): boolean => matchCat(p) && matchDiff(p) && matchAlpha(p);
  // The three filters can hold values that no single puzzle has together. The stalest choice goes
  // first — see `recent` — and `i >= 1` never reaches index 0, the one just tapped: that one is
  // the player's own last word and this loop only relaxes older choices around it, never it
  // itself. Off the library matchCat() is true of everything, so clearing the category there is
  // a no-op and the loop simply moves on. An empty shelf is not something to reconcile: clearing
  // a filter over it would lose the player's choice on the way past a tab that had nothing on it
  // anyway. Nor is a filter alone matching nothing — the three rows now list the whole known
  // universe of values (see catKeys()/diffKeys()/alphaKeys()), not just what's already on the
  // shelf, so that outcome is expected and left to the empty state below, not undone here.
  for (let i = recent.length - 1; i >= 1 && all.length && !all.some(matches); i--) {
    clearFacet(recent[i]);
  }
  for (const el of document.querySelectorAll<HTMLElement>('.tab')) {
    const on = el.dataset.tab === tab;
    el.classList.toggle('on', on);
    el.setAttribute('aria-selected', String(on));
  }
  // L'engrenage de l'en-tête dit aussi où on est, comme un onglet allumé.
  $('prefsOpen').classList.toggle('on', tab === 'prefs');
  $('prefsOpen').setAttribute('aria-pressed', String(tab === 'prefs'));
  // The count answers "is there anything waiting for me" before the tab is even opened. The
  // library needs no such nudge: what's left to discover isn't waiting on anyone.
  badge('flux', shelves.doing.length);

  // Réglages et le calendrier prennent la place de l'étagère, et tout ce qui la sert avec elle :
  // il n'y a rien à filtrer dans une liste de réglages ni dans un mois, et la bannière des packs
  // appartient à la bibliothèque. On sort ici — tout ce qui suit décrit une étagère.
  $('prefsPage').hidden = tab !== 'prefs';
  $('dailyPage').hidden = tab !== 'daily';
  body.hidden = tab === 'prefs' || tab === 'daily';
  if (tab === 'prefs' || tab === 'daily') {
    hideFilters();
    $('packsBanner').hidden = true;
    if (tab === 'prefs') refreshPrefs();
    // Le calendrier se charge tout seul la première fois, puis se repeint depuis son cache : la
    // progression qu'il affiche est locale, donc un retour d'onglet ne coûte aucun réseau.
    else void openDaily();
    return;
  }

  const seg = $('homeFilter');
  seg.hidden = tab !== 'flux';
  $('fltDoing').classList.toggle('on', filter === 'doing');
  $('fltDone').classList.toggle('on', filter === 'done');

  paintBanner();

  // Les trois boutons n'existent que sur la bibliothèque — cf. matchCat/matchDiff/matchAlpha
  // pour pourquoi les trois narrowings s'y sont retirés. Ailleurs on passe une liste vide, et
  // c'est la règle juste en dessous qui escamote la rangée entière.
  // Les trois listes montrent tout l'univers connu — pas seulement ce que l'étagère courante
  // contient — pour que le joueur sache ce qui existe même à zéro résultat pour l'instant.
  const lib = tab === 'library';
  updateFacetButton('cat', cat, lib ? catKeys(all) : []);
  updateFacetButton('diff', diffFilter, lib ? diffKeys() : []);
  updateFacetButton('alpha', alphaFilter, lib ? alphaKeys(all) : []);
  // The bar itself disappears when none of the three narrowings has anything to offer — an
  // empty bar of padding would be one more line for nothing.
  $('homeFacets').hidden = (['cat', 'diff', 'alpha'] as FacetKey[]).every(
    (f) => $(FACET_BTN[f]).hidden,
  );

  const list = all.filter(matches);
  // The shelf itself has puzzles, but none of them survive the active filters — different from
  // an actually empty shelf, and worth a message that points at the filters rather than at the
  // tab. Only reachable now that the narrowing rows can hold a value nothing matches yet.
  const filtered = all.length > 0 && list.length === 0;
  const filteredEmpty = (): HTMLElement =>
    empty('search', t('home.empty.noMatch'), t('home.empty.resetFilters'), resetFilters);
  if (tab === 'create') {
    // Sans création, le titre « Mes créations » et une ligne qui dit qu'il n'y en a pas ENCORE :
    // rien sous le bouton laissait un grand vide, qui se lisait comme une page pas finie de
    // charger (relevé le 2026-10-17). Sans bouton : celui de créer est juste au-dessus.
    body.replaceChildren(
      createHero(),
      mineHeading(),
      list.length ? grid(list) : empty('creation', t('home.empty.noCreations')),
    );
    popIn(body);
    return;
  }
  if (tab === 'library') {
    body.replaceChildren(
      ...(list.length ? libraryShelves(list) : [filtered ? filteredEmpty() : libraryDone()]),
    );
    popIn(body);
    return;
  }
  // La carte d'avis, en tête du flux — sous les DEUX filtres, et c'est land() qui l'impose :
  // le joueur qui vient de finir une feuille revient sur « Terminés », pas sur « En cours »,
  // et c'est précisément le retour qu'on vise. Le flux d'un joueur qui la mérite est court
  // (cf. le commentaire des narrowings : on n'a pas cent puzzles en cours), donc elle ne
  // repousse presque rien — et c'est la seule position vue sans défilement.
  //
  // `rateCardStarted() || !solicitedLanding` : sur un retour sollicité (pub de pause,
  // interstitielle de fin), la carte ne NAÎT pas — deux sollicitations d'affilée — mais une
  // carte déjà née reste : la faire clignoter au gré des pubs se lirait comme un bug d'affichage.
  const card = canShowRateCard() && (rateCardStarted() || !solicitedLanding) ? [rateCard()] : [];
  body.replaceChildren(
    ...card,
    list.length
      ? grid(list)
      : filtered
        ? filteredEmpty()
        : filter === 'doing'
          ? empty('question', t('home.empty.noneDoing'), t('home.empty.openLibrary'))
          : empty('seal', t('home.empty.noneDone')),
  );
  popIn(body);
}

function badge(name: Tab, n: number): void {
  const icon = document.querySelector<HTMLElement>(`.tab[data-tab="${name}"] .tab-i`);
  if (!icon) return;
  icon.querySelector('.tab-n')?.remove();
  if (!n) return;
  const b = document.createElement('i');
  b.className = 'tab-n';
  b.textContent = String(Math.min(99, n));
  icon.appendChild(b);
}

function show(next: Tab): void {
  tab = next;
  if (next === 'flux' || next === 'library' || next === 'create') lastShelf = next;
  paint();
}

/** « Précédent » depuis Réglages : on revient à l'étagère qu'on lisait, et non à l'écran d'avant
 *  au sens du système. C'est le maillon qui remplace closePrefs() dans la chaîne de main.ts —
 *  même contrat, true quand le geste a été consommé.
 *
 *  Le test porte sur `.home.on` autant que sur l'onglet : depuis une feuille de jeu, l'onglet
 *  courant peut très bien être 'prefs' (le joueur y était avant d'ouvrir un puzzle), et là
 *  « précédent » doit refermer la feuille — pas changer un onglet qu'on ne voit pas. */
export function backFromPrefs(): boolean {
  if (tab !== 'prefs' || !$('home').classList.contains('on')) return false;
  show(lastShelf);
  return true;
}

/** Le même maillon pour le calendrier du puzzle du jour : « précédent » ramène à l'étagère qu'on
 *  lisait, et non hors de l'application. Mêmes deux conditions, pour les mêmes raisons. */
export function backFromDaily(): boolean {
  if (tab !== 'daily' || !$('home').classList.contains('on')) return false;
  show(lastShelf);
  return true;
}

/** Nothing to narrow: a message stands where the shelf was, and a filter above it would offer
 *  to sort it. */
function hideFilters(): void {
  $('homeFilter').hidden = true;
  $('homeFacets').hidden = true;
  closeFacetSheet();
}

/**
 * Où atterrir quand la liste vient d'arriver. Deux situations, et **un seul** indice pour les
 * distinguer : `S.puzzleId` est nul au démarrage et ne cesse de l'être qu'une fois une feuille
 * ouverte (il n'est jamais remis à zéro, cf. selectPuzzle). Il dit donc à lui seul si on arrive
 * dans l'application ou si on revient d'une partie — pas besoin d'un drapeau de plus, qui aurait
 * pu se désynchroniser de lui.
 *
 * **On arrive** : le flux s'il y a quelque chose en cours, la bibliothèque sinon. Un flux qui ne
 * contient que du terminé n'est pas un écran d'accueil — c'est une archive, et ouvrir dessus
 * propose au joueur de relire ce qu'il a déjà fini au lieu de lui donner à faire.
 *
 * **On revient** : l'étagère où la feuille qu'on vient de quitter se trouve MAINTENANT, ce qui
 * couvre les deux cas d'un coup — inachevée elle est dans « en cours », terminée dans
 * « terminés ». Se poser sur l'étagère plutôt que sur l'état de la partie a un avantage : c'est
 * la seule règle qui garantisse que la carte cherchée est là. Une feuille ouverte puis quittée
 * sans une seule case posée n'est nulle part dans le flux — elle est restée sur la bibliothèque,
 * et c'est là qu'on retourne, au lieu d'afficher un « en cours » vide.
 */
function land(): void {
  // Un onglet DEMANDÉ passe avant tout le reste : « Voir la bibliothèque » (cf. openHomeOn). Sans
  // ça, la règle ci-dessous ramenait sur « Mon flux » dès que le chien de l'initiation y avait sa
  // carte en cours — le bouton menait ailleurs que là où il disait.
  if (landOn) {
    tab = landOn;
    landOn = null;
    return;
  }
  // Le puzzle du jour d'abord : quand on revient d'une feuille ouverte par une case du
  // calendrier, on revient AU CALENDRIER. La règle des étagères ci-dessous ne saurait pas où le
  // mettre — une feuille quotidienne n'est sur aucune des deux (elle n'est pas dans le
  // catalogue), donc elle atterrirait sur la bibliothèque, loin d'où le joueur était.
  if (openedFromDaily()) {
    tab = 'daily';
    // La vignette et le pourcentage viennent de changer : on recharge le mois affiché.
    refreshDaily();
    return;
  }
  const back = S.puzzleId;
  if (back) {
    const where = shelves.done.some((p) => p.id === back)
      ? 'done'
      : shelves.doing.some((p) => p.id === back)
        ? 'doing'
        : null;
    if (where) {
      tab = 'flux';
      filter = where;
    } else {
      tab = 'library';
    }
    return;
  }
  tab = shelves.doing.length ? 'flux' : 'library';
  if (tab === 'flux') filter = 'doing';
}

/**
 * LES VIGNETTES QUI MANQUENT dans le flux — une réinstallation, ou un autre appareil.
 *
 * La progression voyage, la vignette non (cf. src/thumbs.ts, qui porte le pourquoi). Un joueur
 * qui réinstalle et saisit son code de récupération retrouvait donc ses puzzles terminés sous
 * forme de cartes anonymes : le sceau disait « fini », mais le dessin qu'il avait peint avait
 * disparu. Le calendrier du quotidien réparait déjà ça de son côté depuis des mois ; le flux,
 * jamais.
 *
 * TROIS GARDES, et chacune a sa raison :
 *
 *  - On ne répare QUE l'étagère affichée, et seulement ce qui a une image à montrer. Une
 *    bibliothèque de deux cents feuilles neuves n'a rien à reconstruire.
 *  - EN SÉRIE, et c'est le point coûteux : chaque reconstruction est une grille téléchargée et
 *    un canvas, sur un téléphone. En parallèle, une réinstallation lancerait trente requêtes
 *    d'un coup au moment précis où le joueur attend son écran.
 *  - `PASSE` borne le lot. Au-delà, ce qui reste se répare au passage suivant — l'écran se
 *    rouvre souvent, et une réparation qui s'étale ne se voit pas, là où une qui monopolise la
 *    ligne se voit tout de suite.
 *
 * Chaque carte est retouchée SEULE, comme le calendrier le fait : `paint()` jette toutes les
 * cartes, les ré-anime et renvoie le défilement en haut (cf. shelfSignature) — repeindre la
 * grille à chaque image reconstruite reprendrait au joueur la place où il regarde.
 *
 * `progress` est mise à jour en même temps que le document : sans ça, la prochaine signature
 * d'étagère différerait de ce qui est à l'écran et forcerait la repeinture qu'on vient d'éviter.
 *
 * Un échec ne se voit pas : la carte garde son glyphe de catégorie, qui est ce qu'elle montrait
 * déjà — un message d'erreur pour une image manquante serait du bruit.
 */
const PASSE = 12;
let repairing = false;

async function rebuildShelfThumbs(list: readonly PuzzleSummary[]): Promise<void> {
  if (repairing) return;
  const todo = list
    .filter((p) => {
      const r = progress.get(p.id);
      return r && !r.thumb && (r.done || r.pct > 0);
    })
    .slice(0, PASSE);
  if (!todo.length) return;
  repairing = true;
  try {
    for (const p of todo) {
      const r = progress.get(p.id);
      if (!r || r.thumb) continue;
      const url = await rebuildThumb(p.id, r.done);
      if (!url) continue;
      progress.set(p.id, { ...r, thumb: url });
      // La couverture de CETTE carte, si elle est encore là : le joueur a pu changer d'onglet
      // ou de filtre pendant le téléchargement, et la grille n'est alors plus celle-ci.
      const cover = document.querySelector<HTMLElement>(
        `.home-card[data-id="${CSS.escape(p.id)}"] .home-cover`,
      );
      const mark = cover?.querySelector('.home-mark, .home-pcover');
      if (!cover || !mark) continue;
      const img = document.createElement('img');
      img.alt = '';
      img.src = url;
      mark.replaceWith(img);
    }
  } finally {
    repairing = false;
  }
}

/**
 * Ramène dans le champ la carte du puzzle qu'on vient de quitter — « le cas échéant » : si elle
 * est déjà visible, on ne bouge rien. Faire défiler une liste sous les yeux du joueur pour
 * arriver là où il regarde déjà se lit comme un sursaut, pas comme une aide.
 *
 * Silencieuse quand la carte n'est pas là : un filtre de facette encore actif peut l'avoir
 * écartée de l'étagère, et forcer son affichage voudrait défaire un choix du joueur pour une
 * commodité.
 */
function revealCard(id: string): void {
  const card = document.querySelector<HTMLElement>(`.home-card[data-id="${CSS.escape(id)}"]`);
  if (!card) return;
  const box = $('homeBody');
  const c = card.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  if (c.top >= b.top && c.bottom <= b.bottom) return;
  card.scrollIntoView({ block: 'center', behavior: noMotion() ? 'auto' : 'smooth' });
}

/** La Bibliothèque vide, filtres à part : le joueur a commencé toutes ses feuilles. S'il reste des
 *  packs à débloquer, le message y mène au lieu de dire d'attendre — « reviens, il en arrive »
 *  sous un bandeau qui en propose quatre se contredisait. */
function libraryDone(): HTMLElement {
  const el =
    packsAvailable > 0
      ? empty('seal', t('home.empty.libraryDonePacks'), t('home.empty.seePacks'), () =>
          openPacksPage(),
        )
      : empty('seal', t('home.empty.libraryDone'));
  el.classList.add('library-done');
  return el;
}

/**
 * La bannière des packs, et RIEN D'AUTRE. Bibliothèque seule, et seulement s'il reste quelque
 * chose à débloquer.
 *
 * Détachée de paint() parce qu'elle arrive en retard : le compte des packs est une seconde
 * requête, lancée sans être attendue, et c'est bien — le catalogue ne doit pas dépendre d'un
 * aller-retour de plus pour s'afficher. Mais ce qu'elle faisait à son retour, c'était rappeler
 * paint() ENTIER : la grille était détruite, chaque carte reconstruite et l'ensemble ré-animé
 * par popIn(), une demi-seconde après la première fois. De l'extérieur, ça se voit comme un
 * clignotement, et ça se décrit exactement comme le joueur l'a décrit — « la requête et
 * l'affichage sont faits deux fois ». Ils l'étaient.
 */
function paintBanner(): void {
  const banner = $('packsBanner');
  const wasHidden = banner.hidden;
  banner.hidden = tab !== 'library' || packsAvailable === 0;
  if (banner.hidden) return;
  $('packsBannerTitle').textContent =
    packsAvailable === 1
      ? t('home.packsBanner.one')
      : t('home.packsBanner.sub', { n: packsAvailable });
  // Même seuil que l'introduction de la vitrine (renderList dans packs.ts) : « 1 puzzles à
  // découvrir » n'existe pas, et sous un seul pack d'un seul puzzle le titre dit déjà tout.
  const n = teaserPuzzleCount();
  $('packsBannerSub').textContent = n > 1 ? t('packs.intro', { n }) : '';
  // Les couvertures ne se repeignent qu'à l'apparition du bandeau (ou à l'arrivée de la liste,
  // cf. refreshPacksBanner) : paint() passe ici à chaque filtre touché, et trois canevas
  // redessinés à chaque fois ne montreraient rien de neuf.
  if (wasHidden || !$('packsBannerFan').childElementCount) paintTeaserFan($('packsBannerFan'));
}

/** Met à jour packsAvailable et repeint LA SEULE BANNIÈRE — appelée par loadPuzzles() sans être
 *  attendue par lui, pour que le catalogue s'affiche sans dépendre d'un aller-retour de plus. */
async function refreshPacksBanner(): Promise<void> {
  packsAvailable = await availablePackCount();
  // La liste vient d'arriver : l'éventail est à refaire même si le bandeau était déjà visible.
  $('packsBannerFan').replaceChildren();
  paintBanner();
  // Le message d'une Bibliothèque vide dépend aussi de ce compte (cf. libraryDone) : posé avant
  // qu'il arrive, il dirait « reviens » sous un bandeau qui propose des packs. On ne repeint que
  // ce cas-là — repeindre une étagère pleine rejouerait l'apparition de toutes ses cartes.
  if (tab === 'library' && $('homeBody').querySelector('.home-empty.library-done')) paint();
}

/**
 * Va chercher la liste et repeint. `shouldLand` dit si l'onglet affiché est à REDÉCIDER —
 * c'est toute la différence entre les deux portes exportées juste en dessous, et elle a coûté un
 * bug : voir refreshPuzzles().
 *
 * Rend VRAI quand le catalogue s'est affiché avec des puzzles dedans — pas seulement quand la
 * requête n'a pas levé. C'est ce que demanderUnAvis() a besoin de savoir : une carte de notation
 * posée sur « Impossible de contacter le serveur », ou sur une étagère vide, demanderait un avis
 * au moment précis où l'application n'a rien à montrer.
 */
async function reload(shouldLand: boolean): Promise<boolean> {
  const body = $('homeBody');
  body.setAttribute('aria-busy', 'true');
  // REPEINDRE DE MÉMOIRE AVANT DE DEMANDER. Le catalogue était posé à chaque passage sur un
  // squelette de six cartes grises, y compris en revenant d'une feuille sur une étagère qu'on
  // venait de quitter — puis les vraies cartes revenaient une fraction de seconde plus tard, et
  // les deux images étaient animées par popIn(). De l'extérieur, l'affichage est fait deux fois,
  // et c'est le mot exact d'un joueur.
  //
  // La liste, elle, est déjà là : `served` la garde, et l'avancement se relit localement (le
  // save a été vidé par leaveSheet() juste avant). On peut donc rendre l'étagère EXACTE
  // immédiatement, sans réseau. Le squelette garde tout son sens au premier chargement et après
  // une erreur — là, l'écran est vide et montrer la forme de ce qui arrive vaut mieux que rien.
  // La carte du chien de l'initiation se REFAIT à chaque passage : elle dépend de sa progression,
  // pas du serveur. Gardée telle quelle, elle manquait au premier retour de l'œil (le joueur
  // atterrissait sur la Bibliothèque, son chien en cours introuvable), et elle survivait à
  // « Revoir l'initiation », le temps d'un clignotement dans la Bibliothèque (revue du 2026-10-31).
  const cache = lastServed.length
    ? lastServed.filter((p) => p.id !== INTRO_ID).concat(introCard())
    : null;
  if (cache) {
    progress = progressIndex();
    shelves = sort(orderPuzzles(cache));
    if (shouldLand) land();
    paint();
  } else {
    body.replaceChildren(skeleton());
  }
  try {
    // In parallel, and memoised: the cloud index only decides what the cards display, so it
    // rides alongside the catalogue request instead of adding a round trip in front of it. It
    // never rejects — a failed sync leaves the local percentages, which are still true.
    const token = await currentUser();
    // `mine=1` (cf. fetchPuzzles) sert encore les créations que le serveur garde d'avant le
    // 2026-10-30, le temps du rapatriement ; celles du téléphone se lisent en même temps.
    const [served, mine] = await Promise.all([fetchPuzzles(token), localCreations(), syncOnce()]);
    // Ceinture, le serveur étant déjà la bretelle : il ne sert que les alphabets que ce build
    // déclare (cf. api.ts), mais une feuille dont on n'a pas le dessinateur n'est pas jouable —
    // ses codes au-delà de ceux du repli n'ont pas de pastille, donc pas de moyen d'être peints.
    // Mieux vaut ne pas la proposer que la proposer infinissable.
    // Les créations : celles du téléphone, et celles que le serveur garde encore sans qu'elles
    // soient arrivées ici — AFFICHÉES quand même. Le rapatriement peut échouer (stockage plein,
    // réseau) : les cacher en attendant, c'était les rendre invisibles jusqu'à la purge, qui les
    // aurait effacées sans que le joueur les ait jamais revues (revue du 2026-10-30). Une
    // création supprimée ici et pas encore là-bas (cf. creationsDeleted) ne revient pas.
    const onServer = served.filter((p) => isCreation(p.id));
    creations = mergeCreations(mine, onServer);
    const list = served
      .filter((p) => !isCreation(p.id) && alphabetKnown(p.alphabetId))
      .concat(creations, introCard());
    if (!list.length) {
      hideFilters();
      body.replaceChildren(empty('question', t('home.empty.noPuzzles')));
      return false;
    }
    const avant = cache ? shelfSignature(shelves.doing.concat(shelves.done, shelves.fresh)) : '';
    lastServed = list;
    progress = progressIndex();
    shelves = sort(orderPuzzles(list));
    if (!cache && shouldLand) land();
    // On ne repeint QUE si l'écran apprendrait quelque chose. Sans cette garde, la réponse du
    // serveur redessinait à l'identique la grille peinte de mémoire quelques centaines de
    // millisecondes plus tôt — le deuxième des deux affichages que le joueur voyait.
    if (!cache || avant !== shelfSignature(shelves.doing.concat(shelves.done, shelves.fresh))) {
      paint();
    }
    // Après paint(), pas avant : la carte n'existe dans le document qu'une fois la grille posée.
    if (S.puzzleId) revealCard(S.puzzleId);
    // Les vignettes manquantes se reconstruisent APRÈS le premier rendu, et sans être
    // attendues : l'étagère doit s'afficher tout de suite, et une feuille terminée sur un autre
    // appareil n'est pas une erreur en attendant son image. Les deux étagères du flux
    // seulement — `fresh` ne porte, par définition, aucune progression à illustrer.
    void rebuildShelfThumbs(shelves.doing.concat(shelves.done));
    // Jamais devant le reste : le bandeau est une garniture, pas une donnée que le catalogue
    // attend pour s'afficher. Sa propre repeinture arrive quand la réponse arrive, pas avant.
    void refreshPacksBanner();
    // Le rapatriement, APRÈS l'affichage et sans être attendu : jusqu'à trois requêtes par
    // création, l'une après l'autre, ne doivent pas tenir le squelette à l'écran (revue du
    // 2026-10-30). Il repeint lui-même s'il a déplacé quelque chose.
    if (token && onServer.length) void importServerCreations(token, onServer);
    return true;
  } catch {
    // Le cache est jeté : le prochain passage doit repartir du squelette plutôt que de rendre une
    // étagère dont on n'a pas pu confirmer qu'elle existe encore.
    lastServed = [];
    // SAUF LES CRÉATIONS : elles sont sur le téléphone, et le serveur injoignable n'y change rien.
    // Sans ça, une création faite hors ligne — l'enregistrement n'a plus besoin du réseau — restait
    // introuvable jusqu'au retour du réseau (revue du 2026-10-30). Le catalogue reste en erreur ;
    // seules elles s'affichent, sur leur onglet.
    // Le chien de l'initiation aussi : sa grille est livrée avec l'application (cf. introCard), et
    // un nouveau joueur qui ouvre le jeu hors ligne doit retrouver son dessin en cours.
    const mine = await localCreations();
    const dog = introCard();
    if (mine.length || dog.length) {
      creations = mine;
      lastServed = [...mine, ...dog];
      progress = progressIndex();
      shelves = sort(orderPuzzles(lastServed));
      // À l'arrivée, sur leur onglet — c'est là qu'elles sont toutes ; un retour de partie, lui,
      // reste où il était (cf. land()). Le chien seul, lui, est dans « Mon flux ».
      // Pas quand l'étagère gardée a déjà choisi l'onglet, plus haut (land) : la choisir une
      // seconde fois écrasait ce choix — « Voir la bibliothèque » finissait sur « Mon flux ».
      if (shouldLand && !cache) {
        show(landOn ?? (mine.length ? 'create' : 'flux'));
        landOn = null;
      } else paint();
      return false;
    }
    hideFilters();
    body.replaceChildren(
      empty(
        'warn',
        t('home.empty.serverError'),
        t('home.empty.retry'),
        () => void loadPuzzles(),
        true,
      ),
    );
    return false;
  } finally {
    body.removeAttribute('aria-busy');
  }
}

/**
 * On ARRIVE : le catalogue s'ouvre, ou le joueur revient d'une partie. L'onglet est redécidé par
 * land(), qui est fait pour exactement ces deux moments-là.
 *
 * C'est aussi la porte des boutons « Réessayer » : là, le corps du catalogue a été remplacé par
 * un panneau d'erreur, donc on ne rafraîchit pas une liste affichée — on la reconstruit depuis
 * rien, et atterrir à neuf est le bon comportement.
 */
export async function loadPuzzles(): Promise<boolean> {
  return reload(true);
}

/**
 * La liste a changé, LE JOUEUR NON : on repeint sans le déplacer. Aujourd'hui un seul appelant,
 * `closeReveal()` dans src/packs.ts, après le déblocage d'un pack.
 *
 * Il appelait loadPuzzles(), et c'était un bug à deux détentes. Débloquer un pack n'est ni une
 * arrivée ni un retour de partie — les deux seules situations que land() sait traiter — donc
 * land() y appliquait sa règle d'arrivée et renvoyait le joueur sur « Mon flux » dès qu'une
 * partie y traînait. Le basculement se produisait DERRIÈRE la page Packs, encore posée
 * par-dessus, donc il ne se voyait qu'au moment de la refermer : le joueur accusait la
 * fermeture, qui n'y est pour rien (closePacksPage() ne touche pas à l'onglet).
 *
 * Et la seconde détente est la pire : la récompense du déblocage ne vit QUE sur la Bibliothèque.
 * L'étagère `fresh` est celle de cet onglet (cf. shelf()), et la pastille « Nouveau » est
 * conditionnée à `tab === 'library'` (cf. buildCard()). Le joueur était donc emmené ailleurs à
 * l'instant précis où ce qu'il venait de gagner apparaissait.
 *
 * PRÉSERVER l'onglet plutôt que forcer 'library', même si les deux sont équivalents aujourd'hui :
 * on n'entre dans la page Packs que par #packsBanner, qui n'est visible que sur la Bibliothèque
 * (cf. paint()). Le jour où une deuxième entrée apparaît, le joueur reviendra d'où il venait au
 * lieu d'être déplacé — et la page Packs n'a pas à décider de la navigation du catalogue.
 */
export async function refreshPuzzles(): Promise<boolean> {
  return reload(false);
}

/** L'onglet sur lequel le prochain retour au catalogue doit se poser, ou nul pour la règle
 *  ordinaire (cf. land). Consommé par ce retour. */
let landOn: Tab | null = null;

/** Le catalogue, ouvert sur un onglet précis — « Voir la bibliothèque » au bout de l'initiation. */
export function openHomeOn(next: 'library'): void {
  landOn = next;
  openHome();
}

export function openHome(): void {
  // Une pub seulement à la sortie d'un puzzle qu'on vient tout juste de finir — jamais en
  // rouvrant un puzzle fini plus tôt (S.justFinished ne survit pas à resetPlay(), cf. state.ts).
  // Consommé ici qu'elle s'affiche ou non, pour ne jamais redéclencher au prochain aller-retour
  // sur cette même feuille sans avoir refini quoi que ce soit.
  let breakAd = false;
  let finishedLanding = false;
  if (S.justFinished) {
    S.justFinished = false;
    finishedLanding = true;
    void showInterstitialIfReady();
  } else {
    // Ou bien la pub de pause, au même seuil de porte : une feuille en cours qu'on quitte après
    // une vraie séance. Sans effet au lancement (rien n'a encore été colorié) et pendant les
    // premiers jours d'une installation — voir src/adPacing.ts.
    breakAd = showBreakAdIfDue();
  }
  // Retenu pour la carte d'avis (cf. paint) et la demande d'avis (plus bas) : ni l'une ni
  // l'autre sur un retour sollicité. `finishedLanding` que l'interstitielle de fin soit montée
  // ou non — c'est le comportement qu'avait la session figée, où une feuille finie dans le
  // processus interdisait toute demande à ce retour. Le drapeau tient jusqu'au retour suivant :
  // les deux invitations attendent simplement.
  solicitedLanding = breakAd || finishedLanding;
  // Le rejeu appartient à la feuille : si le joueur la quitte pendant, il s'arrête là et la
  // fin ne se joue pas — une carte « Terminé » n'a rien à faire par-dessus le catalogue.
  animReplayStop();
  // AVANT loadPuzzles(), qui va relire l'index local pour trier les étagères. Une partie
  // terminée s'écrit d'elle-même sur-le-champ (cf. saveProgress), mais une partie en cours est
  // différée de WRITE_MS : sans ce vidage, revenir juste après avoir posé sa première case
  // laisserait le puzzle sur l'étagère « pas touché » et on atterrirait sur la bibliothèque.
  //
  // Et c'est aussi ici que la VIGNETTE se prend, ce qui tombe bien : la carte qu'elle illustre
  // est celle que loadPuzzles() est sur le point de dessiner. Voir leaveSheet().
  leaveSheet();
  $('home').classList.add('on');
  // La demande d'avis attend que le catalogue soit là, et ne part que s'il porte quelque chose.
  // C'est le seul moment calme de l'application : le joueur vient d'arriver sur une étagère et
  // rien ne lui est demandé. Toutes les autres conditions — dont celle qui compte, être REVENU
  // depuis sa dernière partie finie — sont dans peutDemander().
  //
  // Jamais sur un retour sollicité : une interstitielle — de pause OU de fin — puis la carte de
  // Play, c'est deux sollicitations d'affilée au même retour. La demande attend simplement le
  // suivant. La feuille finie doit être exclue ICI et plus par canAskForReview() seul : depuis
  // la session glissante (review.ts), une feuille finie avant une longue pause d'écran compte
  // comme une session passée — « revenu » est alors vrai sur ce retour même.
  void loadPuzzles().then((shown) => {
    if (shown && !solicitedLanding) void askForReview();
  });
}

export function closeHome(): void {
  $('home').classList.remove('on');
  /**
   * La feuille redevient visible : les compteurs de zone repartent PLEINS.
   *
   * Ils s'effacent après deux secondes et demie sans un geste (cf. animPoke et platesAlpha), et
   * ce compte à rebours court aussi sous le catalogue — la boucle dort, mais son minuteur, lui,
   * est un setTimeout. Choisir sa feuille prend plus de trois secondes : le joueur arrivait donc
   * sur une grille dont les compteurs étaient DÉJÀ retirés à 0,12 d'opacité, c'est-à-dire
   * invisibles, et rien ne les ramenait — le réveil de render() est gardé par `!anim.plates`,
   * qui valait encore `true` de la feuille précédente. Il fallait zoomer pour les voir.
   *
   * Ici plutôt que dans `openPuzzle()` : c'est le seul passage par lequel la feuille réapparaît,
   * qu'on vienne d'en ouvrir une nouvelle ou qu'on referme simplement le catalogue sur celle
   * qu'on jouait — et dans le second cas il n'y a pas de `startPlay()` du tout.
   */
  animPoke();
}

/**
 * Le bouton « précédent » d'Android, quand il vient de la feuille : il ramène au catalogue.
 *
 * Il ne le faisait pas. La chaîne de main.ts fermait les panneaux ouverts, et à défaut le
 * natif MINIMISAIT l'application (cf. platform.ts) : depuis une partie en cours, « précédent »
 * envoyait le joueur à l'écran d'accueil du téléphone au lieu de son catalogue. C'est le geste
 * le plus attendu de la plateforme, et c'était le seul chemin de l'interface qu'il ne suivait
 * pas.
 *
 * Rend `false` quand le catalogue est déjà affiché — l'appelant minimise alors, et c'est
 * cette fois le bon comportement : depuis un écran d'accueil, « précédent » sort de l'app.
 */
export function backFromSheet(): boolean {
  if ($('home').classList.contains('on')) return false;
  openHome();
  return true;
}

/**
 * La tête de l'onglet « Créer » : le bouton qui lance l'outil, en grand, et la promesse qui
 * l'accompagne — la photo ne quitte pas le téléphone, le puzzle n'est qu'au joueur. Dite ICI, avant
 * même le premier tap, parce que c'est la question que se pose quelqu'un qui envisage d'y mettre
 * la photo de son chat ou de ses enfants.
 */
function createHero(): HTMLElement {
  const hero = el('div', 'create-hero');
  const go = el(
    'button',
    'create-go',
    `<span class="create-go-i">${icon('i-creation')}</span>
    <span class="create-go-t"></span>
    <span class="create-go-s"></span>`,
  );
  go.querySelector('.create-go-t')!.textContent = t('home.create.title');
  go.querySelector('.create-go-s')!.textContent = t('home.create.sub');
  go.onclick = openCreer;
  // Les deux promesses du récapitulatif de l'outil, mot pour mot : la photo reste sur le téléphone,
  // le puzzle n'est qu'au joueur (demandé le 2026-10-17). C'était une seule note qui disait les
  // deux en une phrase ; la seconde moitié — retrouver son puzzle sur un autre téléphone — y
  // manquait. Les textes viennent du dictionnaire (un <b> dedans) : rien d'extérieur n'entre dans
  // ces innerHTML.
  const note = (id: string, key: 'creer.ready.photo' | 'creer.ready.private') =>
    el('p', 'create-note', `${icon(id)}<span>${t(key)}</span>`);
  hero.append(go, note('i-phone', 'creer.ready.photo'), note('i-lock', 'creer.ready.private'));
  return hero;
}

function mineHeading(): HTMLElement {
  const h = document.createElement('h2');
  h.className = 'create-mine-h';
  h.textContent = t('home.facet.mine');
  return h;
}

/**
 * L'outil de création, CHARGÉ À LA DEMANDE : il embarque la conversion d'image (shared/convert.ts
 * et les encodeurs de tous les alphabets), que le jeu n'avait plus du tout depuis qu'elle était
 * réservée à l'administration. Un import dynamique la garde hors du paquet du démarrage — le
 * catalogue se peint toujours sans elle, et seul qui touche la carte la télécharge.
 */
function openCreer(): void {
  void import('./creer').then((m) => m.openCreer());
}

/** Les créations du téléphone, dont on sait dessiner l'alphabet ; vide si le stockage ne répond
 *  pas — l'écran ne doit pas tomber pour autant. */
async function localCreations(): Promise<PuzzleSummary[]> {
  const mine = await listCreations().catch(() => [] as PuzzleSummary[]);
  return mine.filter((p) => alphabetKnown(p.alphabetId));
}

/** Celles du téléphone, puis celles que le serveur garde encore sans qu'elles soient arrivées ici
 *  — moins celles que le joueur a supprimées (cf. creationsDeleted). La plus récente d'abord. */
function mergeCreations(mine: PuzzleSummary[], onServer: PuzzleSummary[]): PuzzleSummary[] {
  const here = new Set(mine.map((p) => p.id));
  const deleted = creationsDeleted();
  const pending = onServer.filter(
    (p) => !here.has(p.id) && !deleted.has(p.id) && alphabetKnown(p.alphabetId),
  );
  return [...mine, ...pending].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * LES CRÉATIONS SUPPRIMÉES ICI que le serveur garde peut-être encore. Le joueur supprime d'abord
 * sur le téléphone, puis demande l'effacement là-bas ; si cette demande échoue (hors ligne), le
 * rapatriement suivant la ramenait — la création supprimée revenait (revue du 2026-10-30). Leur
 * identifiant est donc noté ici jusqu'à ce que le serveur ait confirmé l'effacement, et le
 * rapatriement ne fait alors que réessayer d'effacer.
 */
const DELETED_KEY = 'codigrid.creationsDeleted';

function creationsDeleted(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DELETED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

function setCreationDeleted(id: string, deleted: boolean): void {
  const all = creationsDeleted();
  if (deleted) all.add(id);
  else all.delete(id);
  try {
    if (all.size) localStorage.setItem(DELETED_KEY, JSON.stringify([...all]));
    else localStorage.removeItem(DELETED_KEY);
  } catch {
    /* pas de stockage : au pire, la création revient une fois */
  }
}

/** Efface la copie du serveur d'une création supprimée ici, et oublie la note si c'est fait. */
async function eraseOnServer(token: string, id: string): Promise<void> {
  await deleteCreation(token, id);
  setCreationDeleted(id, false);
}

/**
 * LE RAPATRIEMENT (2026-10-30) : les créations que le serveur gardait, ramenées sur le téléphone
 * puis effacées là-bas — grille, et sauvegarde de partie avec elle (DELETE /me/creations/:id).
 * Depuis ce jour une création ne quitte plus l'appareil (cf. src/creationStore.ts) ; celles d'avant
 * suivent ce chemin au premier lancement de la version qui le sait, et ce que personne n'aura
 * rapatrié sera purgé côté serveur (cf. backend/src/routes/creations.ts).
 *
 * Dans l'ordre, et chaque pas seulement si le précédent a réussi : la grille écrite ici, la partie
 * réconciliée (settleCreation), PUIS l'effacement là-bas. Un échec en route laisse la création sur
 * le serveur — toujours affichée, cf. mergeCreations —, et le lancement suivant reprend où on en
 * était : `hasCreation` saute la grille déjà là. Une à la fois : vingt au plus, et un lancement ne
 * doit pas tirer soixante requêtes d'un coup.
 *
 * APRÈS le premier affichage, jamais devant (cf. reload), et UNE FOIS par session : un effacement
 * qui échoue ne doit pas tout refaire à chaque retour au catalogue ; le lancement suivant réessaie.
 *
 * Le premier téléphone qui lance la nouvelle version les emporte : un joueur sur deux appareils
 * les retrouve sur celui-là seulement. Accepté avec la décision.
 */
let importing: Promise<void> | null = null;

function importServerCreations(token: string, onServer: PuzzleSummary[]): Promise<void> {
  return (importing ??= (async () => {
    let moved = false;
    // Celles qui restent au serveur à la fin de la passe : elles doivent RESTER affichées (cf.
    // mergeCreations) — la liste reconstruite plus bas les perdait (revue du 2026-10-30).
    const left: PuzzleSummary[] = [];
    // La liste des supprimées est RELUE à chaque pas, pas une fois au départ : le joueur peut en
    // supprimer une pendant que la passe tourne, et une liste figée la faisait rapatrier quand
    // même — elle revenait au lancement suivant (revue du 2026-10-30).
    const deleted = (id: string): boolean => creationsDeleted().has(id);
    for (const p of onServer) {
      try {
        if (deleted(p.id)) {
          await eraseOnServer(token, p.id);
          continue;
        }
        if (!(await hasCreation(p.id))) {
          const grid = await fetchPuzzle(p.id, token);
          // Supprimée pendant le téléchargement : on ne l'écrit pas, on l'efface là-bas.
          if (deleted(p.id)) {
            await eraseOnServer(token, p.id);
            continue;
          }
          await putCreation(p, grid.target);
          moved = true;
        }
        // Et juste avant de rapatrier la partie : réconciliée après la suppression, elle
        // réécrirait une sauvegarde locale pour une feuille qui n'existe plus.
        if (deleted(p.id)) {
          await eraseOnServer(token, p.id);
          continue;
        }
        await settleCreation(token, p.id);
        await deleteCreation(token, p.id);
      } catch {
        left.push(p); // reste sur le serveur, et affichée : le prochain lancement réessaie
      }
    }
    if (!moved) return;
    // Rien ne change à l'écran (les cartes y étaient déjà, cf. mergeCreations), mais la liste
    // doit désormais venir du téléphone : la carte s'ouvrira sans réseau.
    const all = mergeCreations(await localCreations(), left);
    setCreations(all, lastServed.filter((p) => !isCreation(p.id)).concat(all));
  })());
}

/** Combien de créations le joueur garde — pour le décompte des créations offertes (src/creer.ts). */
export function creationCount(): number {
  return creations.length;
}

/**
 * Le bouton retour d'Android pendant la création. L'outil est chargé à la demande, donc main.ts ne
 * peut pas l'importer : il s'inscrit ici à son premier chargement, et la chaîne de main.ts passe
 * par backFromCreer() — faux tant que l'outil n'a jamais été ouvert.
 */
let creerBack: (() => boolean) | null = null;

export function registerCreerBack(fn: () => boolean): void {
  creerBack = fn;
}

export function backFromCreer(): boolean {
  return creerBack?.() ?? false;
}

/**
 * Après une création : elle entre dans les listes, et le joueur revient sur l'onglet « Créer », où
 * elle est la première de « Mes créations » — c'est de là qu'il la lance (décision du 2026-10-16 :
 * « Enregistrer » enregistre, et ne fait rien d'autre dans son dos). Sans réseau : la liste est
 * déjà en mémoire, rien n'est redemandé au serveur.
 */
export function noteCreated(p: PuzzleSummary): void {
  setCreations(
    [p, ...creations.filter((c) => c.id !== p.id)],
    [...lastServed.filter((c) => c.id !== p.id), p],
  );
  show('create');
  $('homeBody').scrollTop = 0;
}

export function wireHome(): void {
  // Le calendrier reçoit ICI sa façon d'ouvrir une feuille : la dépendance pointe dans un seul
  // sens (home connaît daily, jamais l'inverse), comme progress.ts reçoit son pousseur de
  // sauvegardes de sync.ts.
  wireDaily(openPuzzle);
  // La sortie de la feuille : la croix de la barre, et le bouton « précédent » d'Android, qui
  // passe par backFromSheet() depuis la chaîne de main.ts.
  $('sheetClose').onclick = () => openHome();
  $('facetSheetClose').onclick = () => closeFacetSheet();
  // Tapping the backdrop closes, tapping the sheet doesn't — same rule as #prefs (prefs.ts).
  $('facetSheet').onclick = (e) => {
    if (e.target === $('facetSheet')) closeFacetSheet();
  };
  $('packsBanner').onclick = () => openPacksPage();
  // Réglages, monté de la barre du bas à l'en-tête (cf. le type Tab) : toujours la même page, la
  // même place dans la navigation — seule sa porte a changé.
  $('prefsOpen').onclick = () => show('prefs');
  $('fltDoing').onclick = () => {
    filter = 'doing';
    paint();
  };
  $('fltDone').onclick = () => {
    filter = 'done';
    paint();
  };
  for (const el of document.querySelectorAll<HTMLElement>('.tab')) {
    el.onclick = () => {
      // La barre elle-même ne disparaît jamais sous le tap, contrairement à une carte : le
      // petit rebond de l'icône a le temps de se voir.
      if (!noMotion()) {
        el.querySelector('.tab-i')?.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(1)' }],
          { duration: 320, easing: EASE_BACK },
        );
      }
      show(el.dataset.tab as Tab);
    };
  }
}
